/**
 * DJH · Hawaiian heirloom configurator (B-95 / B-103 · D-31)
 *
 * Collects the seven axes, asks ops-web for a price through the Shopify app proxy, and hands the
 * shopper to Shopify's own checkout via the draft order's invoice URL. We are not headless:
 * nothing here rebuilds cart or checkout.
 *
 * Three things in this file are deliberate and easy to "fix" wrongly:
 *
 * 1. A NULL PRICE IS AN ANSWER. `/quote` may return `priceCents: null` — an 18K rate we do not
 *    hold, a chart cell the mill printed ambiguously. That renders as "we'll confirm this one"
 *    and an inquiry route. It never renders as $0, and it never blocks the rest of the page.
 *
 * 2. THE WORKING IS NOT SHOWN. `/quote` returns grams, the wire rate and the labor band so the
 *    counter can explain a price. That is DJH's cost structure. The customer sees the price and
 *    their own choices — never the breakdown.
 *
 * 3. WIDTH IS SHOWN AT REAL SCALE. Dianna, asked what customers regret: the wrong size, and
 *    "not getting the millimeter/width they initially wanted". So width is not a number in a
 *    dropdown; it is a bar drawn next to a US quarter at the same scale, with an optional
 *    card calibration for true physical size. The comparison stays honest even uncalibrated,
 *    because both shapes scale together.
 */
(async function () {
  const root = document.querySelector('[data-djh-hc]');
  const configEl = document.querySelector('[data-djh-hc-config]');
  if (!root || !configEl) return;

  let cfg;
  try {
    cfg = JSON.parse(configEl.textContent);
  } catch (_e) {
    return;
  }

  const form = root.querySelector('[data-djh-hc-form]');
  const $ = (sel) => root.querySelector(sel);
  const mm = window.DJHmmScale;
  if (!mm) return;

  // ── The option chart is the TIER's, read once, never typed here (OPS audit H5/H6; CATALOG 7e7b13678) ──────────────
  // Pieces, profiles, widths, NAMED gauges, ring sizes and bracelet lengths come from GET {proxyBase}/choices: the shop's own
  // wire-weight chart, so a width or gauge the shop does not make can never be offered, and a change there is no theme push.
  // NO FALLBACK LIST: a failed read says the options could not load and leaves the form closed, because a guessed list would be
  // the typed copy this replaced.
  async function loadChoices() {
    try {
      const res = await fetch(cfg.proxyBase + '/choices', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!res.ok) return null;
      const data = await res.json();
      return data && Array.isArray(data.pieces) && data.pieces.length ? data : null;
    } catch (_e) {
      return null;
    }
  }
  const CHOICES = await loadChoices();
  if (!CHOICES) {
    const unavailable = root.querySelector('[data-djh-hc-options-unavailable]');
    if (unavailable) unavailable.hidden = false;
    if (form) form.hidden = true;
    return;
  }

  // ── Option data ───────────────────────────────────────────────────────────────────────────────────────────────
  // Read from CHOICES above. The PRICE never comes from here: only the list of things a customer may pick, and the tier's
  // own answer to "can this be bought online" arrives with each quote (online.sellable, routed, reasons).

  const asNumber = (v) => parseFloat(v && typeof v === 'object' ? (v.size != null ? v.size : v.value != null ? v.value : v.inches) : v);
  /** A piece's chart key: ring, bracelet, pendant:vertical, pendant:horizontal. */
  const chartKey = (pieceType, orientation) => (pieceType === 'pendant' ? 'pendant:' + (orientation === 'horizontal' ? 'horizontal' : 'vertical') : pieceType);
  function chartPiece(pieceType, orientation) {
    return CHOICES.pieces.find((p) => p.key === chartKey(pieceType, orientation)) || null;
  }
  function offeredProfiles(pieceType, orientation) {
    const piece = chartPiece(pieceType, orientation);
    return piece ? piece.profiles.map((p) => p.profile) : [];
  }
  function chartWidths(pieceType, profile, orientation) {
    const piece = chartPiece(pieceType, orientation);
    const prof = piece && piece.profiles.find((p) => p.profile === profile);
    return prof ? prof.widths : [];
  }
  /** The named gauges the chart holds for ONE width of one profile of one piece: [{ label, gauge }]. */
  function gaugesFor(pieceType, profile, widthMm, orientation) {
    const row = chartWidths(pieceType, profile, orientation).find((w) => Number(w.widthMm) === Number(widthMm));
    return row ? (row.gauges || []).map((g) => ({ label: g.label, gauge: g.gaugeMm })) : [];
  }
  const RING_SIZES = (CHOICES.ringSizes || []).map(asNumber).filter(Number.isFinite);

  // A metal color the tier does not sell online is marked as quoted with the shop, from the tier's own list (online.metalColors).
  (function markQuotedColors() {
    const sel = form && form.querySelector('[name="metal_color"]');
    const online = CHOICES.online && Array.isArray(CHOICES.online.metalColors) ? CHOICES.online.metalColors.map((c) => String(c).toLowerCase()) : null;
    const label = sel && sel.getAttribute('data-quoted-label');
    if (!sel || !online || !label) return;
    Array.prototype.forEach.call(sel.options, (opt) => {
      if (online.indexOf(String(opt.value).toLowerCase()) === -1) opt.textContent = opt.value + ' · ' + label;
    });
  })();

  // THE SAME SETTING THE CHART IS DRAWN FROM. Parsed here so the picker's options, its
  // default and its warning all move together when the bench rule moves — one source,
  // three surfaces. Rows are "orientation|inches|maxLetters".
  const PENDANT_RULE = String(cfg.pendantLengthRule || '')
    .split('\n')
    .map((line) => line.split('|').map((part) => part.trim()))
    .filter((parts) => parts.length === 3)
    .map((parts) => ({ orientation: parts[0], inches: parseFloat(parts[1]), maxLetters: parseInt(parts[2], 10) }))
    .filter((row) => Number.isFinite(row.inches) && Number.isFinite(row.maxLetters));

  function pendantRuleFor(orientation) {
    const rows = PENDANT_RULE.filter((r) => r.orientation === orientation);
    return rows.length ? rows.slice().sort((a, b) => a.inches - b.inches) : [];
  }

  // SPACES COUNT. Henry, 2026-08-31: "If you are asking about 'Kuu Lei', the way you laid
  // it out, that's 7 letters. However, in our product … this is 6 letters since there is
  // no space. spaces count as letters."
  //
  // TRIM THE ENDS ONLY, AND DO NOT COLLAPSE RUNS. A trailing space the customer has not
  // finished typing is not engraved; a space between two words is, and two spaces in the
  // middle are two characters on the bar. The bar does not care why a character is blank.
  //
  // This was non-space counting on my own flagged assumption, and it was the wrong one —
  // in the direction that costs the customer: "Kuu Lei" was recommended 1.5" as a
  // six-letter name and is a seven-letter name, so a two-word name was steered to a
  // pendant too short for it. The bench would have caught it at design approval, which
  // is the checkpoint this whole warn-and-allow design leans on — but as a conversation
  // nobody needed to have.
  //
  // THE REFERENCE PRODUCT COULD NOT HAVE SETTLED IT. The engraved sample everyone looked
  // at is "Kuulei" — six characters, no space, six under either rule. The example was
  // blind to the question it was being used to answer.
  //
  // This is the ONLY place a name's letters are counted; the parity check builds its
  // probe names from a single repeated character, so it reads the same under both rules.
  function nameLetterCount() {
    return Array.from(String(state.name || '').normalize('NFC')).length;
  }

  // HARD STOP, D-357 (Henry, 2026-10-24): the name field holds no more letters than the chosen length carries. The server counts
  // again and is the authority; this stop is a courtesy so nobody types a name the bar cannot hold.
  function pendantRowFor(inches) {
    return pendantRuleFor(state.orientation).filter((r) => r.inches === inches)[0] || null;
  }

  const BRACELET_LENGTHS = (CHOICES.braceletLengths || []).map(asNumber).filter(Number.isFinite);

  /** Ground follows the lettering fill — the customer never picks it (Mike + Dianna, 2026-08-15). */
  const GROUND_BY_FILL = {
    flat_enamel: cfg.copy.groundChipChop,
    raised: cfg.copy.groundSand,
    raised_enamel: cfg.copy.groundSand,
  };
  const FILL_IS_ENAMEL = { flat_enamel: true, raised: false, raised_enamel: true };

  /**
   * Enamel appears in two independent places on a Hawaiian heirloom piece — the lettering and
   * the edge — and Henry named the second one on 2026-08-21: "there is a polished border,
   * diamond cut border, and enameled border option". Matched on the value rather than a fixed
   * list because the edge options are a theme setting the shop can edit; a merchant renaming it
   * "Enamel border" should not silently lose the color question.
   */
  /**
   * THE PLAIN EDGE — one answer, read by the two places that used to compute it separately.
   *
   * Henry has said it three ways: `None` is not a value, empty is not an option, and the
   * plain edge is High Polish. Two places disagreed about how to find it, and agreed only by
   * coincidence: the initial state took `cfg.borders[0]`, and the enamel-floor fallback took
   * the first non-enamel option. Both were positional, so reordering the setting would have
   * moved one, or both, or neither. That is the two-spellings shape, in miniature.
   *
   * VALIDATED AGAINST THE RENDERED LIST rather than trusted. A `default_border` naming a
   * value that is not in `borders` would put a choice on the order the shop does not offer,
   * so it falls through to the first real one. And it must never resolve to an enamel border:
   * enamel needs 10 mm, so an enamel default would be disabled the moment a customer picked
   * a narrow band — a default that cannot be chosen is worse than a positional one.
   */
  function plainBorder() {
    const list = (cfg.borders || []).map((b) => String(b).trim()).filter(Boolean);
    const isEnamel = (v) => String(v).toLowerCase().indexOf('enamel') !== -1;
    // `None` IS A BORDER, AND IT IS NOT THE PLAIN ONE (D-94). It names the design carved
    // through to the edge; High Polish is the plain edge. Since D-94 put None first in the
    // list, "first non-enamel" would now resolve to it — so the fallback skips it as well.
    // A reset that lands on None would change what the piece IS, not just its finish.
    const isNone = (v) => String(v).trim().toLowerCase() === 'none';
    const named = String(cfg.defaultBorder || '').trim();
    if (named && list.indexOf(named) !== -1 && !isEnamel(named) && !isNone(named)) return named;
    return list.find((b) => !isEnamel(b) && !isNone(b)) || list[0] || '';
  }

  /**
   * The carving finish as a person reads it. Falls back to the raw value rather than to a
   * label, so a value the settings do not name still reaches the bench as itself instead of
   * silently becoming the other one.
   */
  function finishLabel() {
    // `None` is a VALUE, not a blank — D-94/D-95. It names a craft outcome (the design
    // carved through to the edge) and must stay distinguishable from never-asked, exactly
    // like `Design: none`. So it is returned as itself rather than falling to ''.
    if (state.designFinish === 'None') return 'None';
    if (state.designFinish === 'raised') return cfg.copy.labelFinishRaised || 'raised';
    if (state.designFinish === 'sand') return cfg.copy.labelFinishSand || 'sand';
    return String(state.designFinish || '');
  }

  /**
   * D-93 · Raised carving needs width above the detail floor.
   *
   * AT THE FLOOR AND ABOVE the customer chooses. BELOW IT sand is STATED — not a disabled option,
   * not a greyed choice, not a warning. Henry: "the raised design doesn't apply to hawaiian
   * bracelets 10mm and below. unfortunately, it's too small... the smaller designs will
   * default to sand design_finish." Raised at 8 mm is not a worse-looking piece; it is not a
   * piece, and a control that shows it as unavailable frames a fact about the craft as a
   * refusal.
   *
   * GATED ON WIDTH, NEVER ON PIECE TYPE. Henry's "it usually doesn't apply for rings and
   * pendants" is a consequence of most of them being narrow, not a rule about rings. Rings
   * run to 15 mm and pendants to 40 mm, and those DO get the choice. Reading "usually" as a
   * piece-type exclusion is how a consequence becomes a rule the third time it is copied.
   *
   * THE VALUE IS FORCED, NOT JUST THE UI. Someone can pick raised at 15 mm and then narrow
   * the band; the radio would still say raised and payload() would carry it to the bench. So
   * state.designFinish is set to sand here, the same way the enameled border resets rather
   * than staying selected-but-disabled.
   */
  function renderDesignFinish() {
    const wrap = $('[data-djh-hc-design-finish-wrap]');
    if (!wrap) return;
    const choice = $('[data-djh-hc-finish-choice]');
    const stated = $('[data-djh-hc-finish-stated]');
    const hint = $('[data-djh-hc-finish-hint]');

    // THREE STATES, NOT TWO — CATALOG named them choice / fixed / unknown, and the theme
    // mirrors them because it cannot call designFinishFor() across the tier boundary.
    //
    // NO WIDTH IS NOT A SMALL WIDTH. Showing "sand" before a width is chosen states a fact
    // about the carving nobody established. And NO FLOOR IS NOT A FLOOR OF ZERO: if the
    // vocabulary carries no minimum, nobody has said where raised stops working, so
    // offering it would be a guess in the other direction. Either unknown ⇒ say nothing.
    const floor = Number(cfg.detailFloorMm);
    const floorKnown = Number.isFinite(floor) && floor > 0;
    const widthKnown = Number.isFinite(state.widthMm) && state.widthMm > 0;
    // A name-only piece has no design to carve: no Design finish row (Henry, 2026-10-05).
    const plain = state.design === 'none';
    wrap.hidden = plain || !(floorKnown && widthKnown);
    if (plain || !floorKnown || !widthKnown) return;

    // INCLUSIVE AT THE FLOOR. Henry, 2026-08-25: "raised design can be for 10mm but that's
    // the limit." So raised STARTS at the floor — `width < floor` takes sand, and 10 mm
    // itself offers the choice.
    //
    // 🔴 THIS WAS WRONG BY ONE AND IT WAS LIVE. The first spec said `width <= floor` → sand,
    // so a customer at exactly 10 mm was told raised was unavailable for a piece the bench
    // makes. CATALOG found it by cross-tabulating 180 photographs of real bracelets: TWELVE
    // are carved raised at exactly 10 mm — pieces that exist and that the rule said could
    // not.
    //
    // AND THE TEST WAS PINNING THE BUG. A mutation proving "weaken <= to < and the suite
    // fails" was true and useless: it proved the boundary was strict, not that strict was
    // right. Correcting the rule would have failed a test telling whoever did it they had
    // broken something. A test proves the code matches the spec; only the shop's own
    // inventory proves the spec matches the bench.
    //
    // Note the shape: a constant named MIN compared with `>` means "more than the minimum".
    // Both halves agreed with each other, so nothing read as wrong.
    // ── D-95 AMENDED 2026-08-30 · THE BORDER NO LONGER STATES THE FINISH ──
    //
    // There was a branch here: a Cut Out border set the finish to None and HID the choice,
    // printing "the design is carved right through to the edge, so there is no ground left
    // to work". It came from 38 of 38 pieces in 180 photographs agreeing in both
    // directions, then confirmed by Henry.
    //
    // The correlation was real. The causal claim built on top of it was too strong. Henry,
    // with the bracelet in his hand: "after reviewing the piece, it is also sand finish on
    // the interior. some areas are too small to cut so it's sand finish… and yes, i already
    // looked and zoomed in to verify."
    //
    // Cut Out means OPENWORK, not everything removed — the same thing D-122 settled about
    // that word. Where a design is too fine to pierce, metal remains, and the ground that
    // survives takes a finish like any other ground.
    //
    // 🔴 AND D-95 WARNED ABOUT PRECISELY THE ERROR IT THEN MADE. Its own text said "38
    // photographs agreeing is a hypothesis, not a rule, and the same evidence would have
    // supported the opposite build" — and it built the rule anyway. Writing the caveat down
    // is not the same as heeding it. What settled this was not more photographs: it was a
    // person with the piece and a magnifier.
    //
    // The width branches (D-93) are untouched. Only the border branch is gone.
    if (state.widthMm < floor) {
      state.designFinish = 'sand';
      const sandInput = form && form.querySelector('[name="design_finish"][value="sand"]');
      if (sandInput) sandInput.checked = true;
    }

    const offersRaised = state.widthMm >= floor;

    if (choice) choice.hidden = !offersRaised;
    if (stated) {
      stated.hidden = offersRaised;
      stated.textContent = offersRaised
        ? ''
        : String(cfg.copy.designFinishStated || '').replace('[mm]', String(floor));
    }
    // The hint explains a choice. Where there is no choice, the stated sentence is the whole
    // message and a second line under it would be explaining something nobody was offered.
    if (hint) hint.hidden = !offersRaised;
  }

  /**
   * THE LENGTH IS THE LIMIT (D-357, Henry 2026-10-24: "hard stop at the chosen length"). The name field holds no more
   * letters than the chosen length carries; spaces count. Typing past it stops, and the page says so in a plain sentence
   * that offers the next length. It replaces the older warn-and-allow guidance, which let a name outgrow its bar.
   *
   * A length too short for a name already in the field (a link that arrives with both) is disabled in the list, and the
   * shortest length that fits is chosen.
   */
  function renderPendantLength() {
    const sel = $('[data-djh-hc-pendant-length]');
    const hint = $('[data-djh-hc-pendant-hint]');
    const longer = $('[data-djh-hc-pendant-longer]');
    const nameField = $('[name="name"]');
    const rows = pendantRuleFor(state.orientation);
    if (!sel || !rows.length) return;

    const lengths = rows.map((r) => r.inches).filter((v, i, a) => a.indexOf(v) === i);
    const letters = nameLetterCount();
    const fits = rows.filter((r) => letters <= r.maxLetters).map((r) => r.inches);
    if (!Number.isFinite(state.pendantLengthIn)) state.pendantLengthIn = lengths[0];
    if (fits.length && fits.indexOf(state.pendantLengthIn) === -1) state.pendantLengthIn = fits[0];
    if (!fits.length) state.pendantLengthIn = lengths[lengths.length - 1];

    const picked = fillSelect(sel, lengths, state.pendantLengthIn, (v) => String(v) + ' in');
    if (Number.isFinite(picked)) state.pendantLengthIn = picked;
    Array.prototype.forEach.call(sel.options, (opt) => {
      opt.disabled = fits.indexOf(parseFloat(opt.value)) === -1 && fits.length > 0;
    });

    const row = pendantRowFor(state.pendantLengthIn) || rows[0];
    if (nameField) nameField.maxLength = 60;
    // A name that arrives longer than the longest bar (a link, or a switch of orientation) is cut to it, with the stop sentence.
    if (!fits.length && holdNameToLength(nameField)) readForm();

    if (longer) longer.hidden = true;
    if (!hint) return;
    const next = rows.filter((r) => r.inches > row.inches)[0] || null;
    const lenLabel = String(row.inches) + ' in';
    if (state.nameStopped && letters >= row.maxLetters) {
      if (next) {
        hint.textContent = String(cfg.copy.pendantStopLonger || '')
          .replace('[len]', lenLabel).replace('[max]', String(row.maxLetters))
          .replace('[next]', String(next.inches) + ' in').replace('[nextmax]', String(next.maxLetters));
        if (longer) {
          longer.textContent = String(cfg.copy.pendantChooseLonger || '').replace('[next]', String(next.inches) + ' in');
          longer.dataset.djhHcNextLength = String(next.inches);
          longer.hidden = false;
        }
      } else {
        hint.textContent = String(cfg.copy.pendantStopTop || '').replace('[len]', lenLabel).replace('[max]', String(row.maxLetters));
      }
      return;
    }
    state.nameStopped = false;
    hint.textContent = letters
      ? String(cfg.copy.pendantCount || '').replace('[n]', String(letters)).replace('[max]', String(row.maxLetters))
      : '';
  }

  // What the workshop can cut in a name: A-Z, the Latin-1 and Latin Extended-A letters (kahakō vowels are precomposed after NFC; a
  // macron on a consonant has no such form and is refused), the ʻokina, curly quotes, apostrophe, hyphen, period and space. The server
  // holds the same set (modules/catalog/orders/engraving.ts ENGRAVABLE) and is the authority; this keeps an emoji or a symbol out
  // of the field and says why, in plain words.
  const ENGRAVABLE_CHAR = /^[A-Za-z\u00c0-\u00ff\u0100-\u017f\u02bb\u02bc\u2018\u2019'\-. ]$/;

  let rejectedTimer;
  function keepNameEngravable(field) {
    const note = $('[data-djh-hc-name-rejected]');
    if (!field) return;
    const chars = Array.from(String(field.value || '').normalize('NFC'));
    const bad = chars.filter((c) => !ENGRAVABLE_CHAR.test(c));
    if (bad.length) field.value = chars.filter((c) => ENGRAVABLE_CHAR.test(c)).join('');
    // The sentence stays up for a few seconds, so the next keystroke does not take it away before it is read.
    if (note && bad.length) {
      note.hidden = false;
      note.textContent = String(cfg.copy.nameRejected || '').replace('[chars]', Array.from(new Set(bad)).map((c) => '\u201c' + c + '\u201d').join(' '));
      window.clearTimeout(rejectedTimer);
      rejectedTimer = window.setTimeout(() => { note.hidden = true; note.textContent = ''; }, 6000);
    }
  }

  // Cuts the typed name back to what the chosen length carries. Returns true when it had to cut.
  function holdNameToLength(field) {
    if (!field || state.pieceType !== 'pendant') return false;
    const row = pendantRowFor(state.pendantLengthIn);
    if (!row) return false;
    const chars = Array.from(String(field.value || '').normalize('NFC'));
    if (chars.length <= row.maxLetters) return false;
    field.value = chars.slice(0, row.maxLetters).join('');
    state.nameStopped = true;
    return true;
  }

  function borderIsEnamel() {
    return String(state.border || '').toLowerCase().indexOf('enamel') !== -1;
  }

  /**
   * Henry, 2026-08-21: "many customers also like to have it match the enamel letter coloring.
   * however, a caveat. if we do enamel border, usually a 10mm is the smallest."
   *
   * Two separate things, and they are deliberately implemented differently:
   *
   *  - MATCHING IS A DEFAULT, NOT A RULE. The edge color follows the lettering color until
   *    the customer touches it, then it stops following. Someone who wants a contrasting edge
   *    is not making a mistake, and coupling the two would invent a rule he did not state.
   *
   *  - THE 10 MM FLOOR IS A RULE, and it disables the option rather than moving the width.
   *    Width is the one thing this page exists to get right — it is drawn at physical scale
   *    beside a quarter because Dianna said the top regret is "not getting the
   *    millimeter/width/length they initially wanted". Silently bumping a chosen width to
   *    unlock an edge finish would cause the exact regret the drawing is there to prevent.
   *    So: the customer keeps their width, the edge option greys out, and the page says why.
   *
   * This is a HINT, not a guard. A crafted request never touches this file, so the same floor
   * has to hold where the price is computed — raised to CATALOG, not closed here.
   */
  /**
   * Point the sizing link at the right part of the guide.
   *
   * /pages/sizing measures a bangle and a ring by different procedures and carries an
   * anchor for each — id="bangle" and id="ring", both present since the guide was
   * written. Every link in the theme pointed at the top of the page, which is the
   * bangle block, so a customer configuring a RING was sent to bracelet instructions.
   * Henry found it on a ring PDP; this is the same bug in the configurator, where one
   * setting cannot serve three piece types.
   *
   * Rewrites only the fragment, so the base URL stays whatever the setting says.
   */
  function renderSizingLink() {
    const link = $('[data-djh-hc-sizing-link]');
    if (!link) return;
    const base = (link.getAttribute('href') || '').split('#')[0];
    if (!base) return;
    const anchor = state.pieceType === 'ring' ? 'ring' : 'bangle';
    link.setAttribute('href', base + '#' + anchor);
  }

  function renderBorder() {
    const sel = form && form.querySelector('[name="border"]');
    if (!sel) return;
    const floor = cfg.enamelBorderMinWidthMm || 0;
    const tooNarrow = floor > 0 && state.widthMm < floor;

    // The same plain edge the form opens on — not "the first option that happens not to be
    // enamel", which is what this used to compute independently.
    const fallback = plainBorder();
    for (let i = 0; i < sel.options.length; i += 1) {
      const opt = sel.options[i];
      if (String(opt.value).toLowerCase().indexOf('enamel') !== -1) opt.disabled = tooNarrow;
    }

    // Reached by lowering the width with an enameled border already chosen. A disabled option
    // that stays selected is still submitted, so the selection has to actually move.
    if (tooNarrow && borderIsEnamel()) {
      sel.value = fallback;
      state.border = fallback;
    }

    const note = $('[data-djh-hc-border-floor]');
    if (note) {
      note.hidden = !tooNarrow;
      note.textContent = tooNarrow
        ? String(cfg.copy.enamelBorderFloorNote || '').replace('{mm}', String(floor))
        : '';
    }

    // Toggled here rather than in renderLettering() because the reset above can clear an
    // enameled border, and asking for its color afterwards would be asking about a choice the
    // customer no longer has.
    const borderEnamelWrap = $('[data-djh-hc-border-enamel-wrap]');
    if (borderEnamelWrap) borderEnamelWrap.hidden = !borderIsEnamel();

    // Matching is the common case, so it is the starting point — not a constraint.
    const colorSel = form && form.querySelector('[name="border_enamel_color"]');
    if (colorSel && !state.borderEnamelTouched && FILL_IS_ENAMEL[state.fill]) {
      const want = state.customEnamel ? '__custom__' : state.enamelColor;
      if (want) {
        colorSel.value = want;
        state.customBorderEnamel = state.customEnamel;
        if (!state.customEnamel) state.borderEnamelColor = state.enamelColor;
      }
    }
  }

  /** The widths the shop's chart carries for this piece and profile (the tier's list, in chart order). */
  function availableWidths(pieceType, profile) {
    return chartWidths(pieceType, profile, state.orientation).map((w) => w.widthMm);
  }

  // ── State ─────────────────────────────────────────────────────────────────────────────────

  const state = {
    // NO DEFAULT PIECE. It was 'bracelet', which meant a visitor who never answered the
    // first question still got a bracelet quoted at them. Null until they choose; every
    // consumer below already branches on pieceType, and gatePieceType() keeps the form
    // shut until it is set.
    pieceType: null,
    // null until the rule sets it; the name is held to this length (D-357).
    pendantLengthIn: null,
    shopNote: '',
    nameStopped: false,
    // NO DEFAULT DESIGN, for the same reason pieceType has none: a preselected value is a
    // choice nobody made, and on an heirloom the design is the piece. Null until chosen.
    design: null,
    designName: '',
    // SAND OR RAISED — how the CARVING is worked, and a customer choice (Henry,
    // 2026-08-25). NOT lettering_finish, which GROUND_BY_FILL derives below and which
    // describes the ground behind the LETTERS. They share the word `sand`; conflating them
    // once already orphaned this field for four days.
    designFinish: 'sand',
    profile: 'barrel',
    widthMm: null,
    gaugeMm: null,
    gradeLabel: '',
    karat: 14,
    metal: 'gold',
    metalColor: cfg.metalColors[0] || 'Yellow',
    // Henry, 2026-08-19: "default size is typically a 7 1/2 for bracelets and rings" — the same
    // number in two different units, which is why he states it as one rule.
    lengthIn: 7.5,
    ringSize: 7.5,
    hinge: false,
    fill: 'raised',
    // THE CATALOGUE'S OWN THREE STRINGS: horizontal · vertical · not applicable,
    // lowercase, space in the third. "not applicable" is a real answer for a bracelet
    // or a ring — an empty string would be indistinguishable from a question that was
    // never asked, which is the distinction this file keeps making everywhere else.
    orientation: 'not applicable',
    // '' | 'slider' | 'chain'. '' is a real value and not a missing one: a pendant
    // with no construction chosen prices exactly as it did before this question
    // existed, which is what keeps this from silently making every pendant an inquiry.
    construction: '',
    chainMm: null,
    chainLengthIn: null,
    style: cfg.letterStyles[0] || 'Old English',
    // The FIRST value is the norm ("Standard"), not an absence. Henry's list is
    // standard / round / plumeria flower — choosing nothing gets Standard, and the piece
    // is engraved with one either way, so the state starts there rather than empty.
    chevron: (cfg.chevrons && cfg.chevrons[0] ? String(cfg.chevrons[0]).trim() : ''),
    enamelColor: cfg.enamelColors[0] || 'Black',
    customEnamel: false,
    // THE PLAIN EDGE, NAMED — see plainBorder() below. Not cfg.borders[0]: being first in
    // the list made High-Polish the default by accident, and a reorder would have silently
    // changed what a customer gets.
    border: plainBorder(),
    borderEnamelColor: cfg.enamelColors[0] || 'Black',
    customBorderEnamel: false,
    borderEnamelTouched: false,
    name: '',
    inside: '',
    addons: [],
  };

  let quote = null; // last successful quote payload
  let seq = 0; // request sequence — a slow response must never overwrite a newer one

  // ── Real-scale drawing ────────────────────────────────────────────────────────────────────

  /**
   * The width comparator, at the width decision.
   *
   * Draws every width this piece and profile can take, side by side at physical scale
   * against a US quarter — the strip that used to run twice on the browse page before a
   * customer saw a single piece, now sitting where someone is actually choosing.
   *
   * BUILT FROM THE SELECT, NOT FROM A SETTING. The old browse comparator carried its own
   * `widths` list, which is how the bracelet page ended up advertising 6 mm and 8 mm
   * bracelets that do not exist — two lists of widths, and nothing keeping them honest.
   * Reading the options the select already holds means the strip cannot offer a width the
   * form would refuse, whatever the piece or profile.
   *
   * Clicking a band selects that width, so the comparison IS the control rather than a
   * picture beside one. The select stays authoritative and stays keyboard-reachable; with
   * no JS this strip never appears and nothing is lost.
   */
  function renderCompare() {
    const wrap = $('[data-djh-hc-compare-wrap]');
    const list = $('[data-djh-hc-compare]');
    const sel = $('[data-djh-hc-width]');
    if (!wrap || !list || !sel) return;

    const widths = [...sel.options].map((o) => parseFloat(o.value)).filter(Number.isFinite);
    // One width is not a comparison. Hiding it beats drawing a lone band next to a coin and
    // calling it a choice.
    wrap.hidden = widths.length < 2;
    if (wrap.hidden) return;

    const scale = mm.pxPerMm();
    const coin = $('[data-djh-hc-compare-coin]');
    if (coin) {
      coin.style.width = mm.QUARTER_MM * scale + 'px';
      coin.style.height = mm.QUARTER_MM * scale + 'px';
    }

    // Rebuild only when the width list itself changed; otherwise just rescale, so a
    // calibration drag does not thrash the DOM on every pointer move.
    const signature = widths.join(',');
    if (list.dataset.djhSignature !== signature) {
      [...list.querySelectorAll('[data-djh-hc-compare-item]')].forEach((el) => el.remove());
      widths.forEach((w) => {
        const li = document.createElement('li');
        li.className = 'djh-hc__compare-item';
        li.setAttribute('data-djh-hc-compare-item', String(w));

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'djh-hc__compare-btn';
        btn.setAttribute('aria-label', cfg.copy.actualSizeAria.replace('{width}', String(w)));

        const band = document.createElement('span');
        band.className = 'djh-hc__compare-band';
        btn.append(band);

        const label = document.createElement('span');
        label.className = 'djh-hc__compare-mm';
        label.textContent = String(w);

        btn.addEventListener('click', () => {
          sel.value = String(w);
          sel.dispatchEvent(new Event('change', { bubbles: true }));
        });

        li.append(btn, label);
        list.append(li);
      });
      list.dataset.djhSignature = signature;
    }

    list.querySelectorAll('[data-djh-hc-compare-item]').forEach((li) => {
      const w = parseFloat(li.getAttribute('data-djh-hc-compare-item'));
      const band = li.querySelector('.djh-hc__compare-band');
      if (band) band.style.height = w * scale + 'px';
      const chosen = state.widthMm === w;
      li.classList.toggle('is-chosen', chosen);
      const btn = li.querySelector('button');
      if (btn) btn.setAttribute('aria-pressed', chosen ? 'true' : 'false');
    });
  }

  function drawScale() {
    const scale = mm.pxPerMm();
    // [data-djh-hc-bar] IS THE WIDTH BAND, not the submit bar. A blanket rename of the
    // selector on 2026-08-30 captured this line too, so drawScale started sizing the
    // sticky submit bar as though it were a 6mm band — inline height 22.6px and a
    // "drawn to scale" aria-label on a button — while the real band went undrawn.
    const bar = $('[data-djh-hc-bar]');
    const coin = $('[data-djh-hc-coin]');
    const label = $('[data-djh-hc-bar-label]');
    if (bar && state.widthMm) {
      bar.style.height = state.widthMm * scale + 'px';
      bar.setAttribute('aria-label', cfg.copy.actualSizeAria.replace('{width}', state.widthMm));
    }
    if (coin) {
      coin.style.width = mm.QUARTER_MM * scale + 'px';
      coin.style.height = mm.QUARTER_MM * scale + 'px';
    }
    if (label) label.textContent = state.widthMm ? state.widthMm + ' mm' : '';

    const card = $('[data-djh-hc-card]');
    if (card) card.style.width = mm.CARD_MM * scale + 'px';
    const claim = $('[data-djh-hc-scale-claim]');
    if (claim) claim.textContent = mm.isCalibrated() ? cfg.copy.scaleCalibrated : cfg.copy.scaleUncalibrated;

    // Same scale, same moment — a calibrated quarter that only fixed one of the two
    // drawings would make them disagree on screen.
    renderCompare();
  }

  // ── Rendering the dependent controls ──────────────────────────────────────────────────────

  function fillSelect(sel, values, current, formatter) {
    if (!sel) return;
    const fmt = formatter || ((v) => String(v));
    sel.innerHTML = '';
    values.forEach((v) => {
      const opt = document.createElement('option');
      opt.value = String(v);
      opt.textContent = fmt(v);
      sel.appendChild(opt);
    });
    const has = values.some((v) => String(v) === String(current));
    sel.value = String(has ? current : values[0]);
    return parseFloat(sel.value);
  }

  // A WIDTH WE DON'T MAKE FOR THIS PIECE MOVES TO THE NEAREST ONE, AND SAYS SO.
  // It used to fall to the first option in silence: arrive on "Design this width: 40 mm",
  // pick Ring, and the form read 4 mm with nothing to say why (Integration, H-25,
  // 2026-09-14). The customer's width is a choice; changing it without a word is the
  // exact regret this page exists to prevent.
  function nearestWidth(widths, wanted) {
    return widths.reduce((best, w) => (Math.abs(w - wanted) < Math.abs(best - wanted) ? w : best), widths[0]);
  }

  function renderWidths() {
    // A profile this piece's chart does not carry (a flat-only piece while barrel is checked) moves to the first one it does,
    // radio included, rather than leaving an empty width list.
    const offered = offeredProfiles(state.pieceType, state.orientation);
    if (offered.length && offered.indexOf(state.profile) === -1) {
      state.profile = offered[0];
      const radio = form && form.querySelector('[name="profile"][value="' + state.profile + '"]');
      if (radio) radio.checked = true;
    }
    const widths = availableWidths(state.pieceType, state.profile);
    const wanted = state.widthMm;
    const note = $('[data-djh-hc-width-note]');
    let target = wanted;
    let moved = false;
    if (Number.isFinite(wanted) && widths.length && widths.indexOf(wanted) === -1) {
      target = nearestWidth(widths, wanted);
      moved = true;
    }
    const picked = fillSelect($('[data-djh-hc-width]'), widths, target, (v) => v + ' mm');
    if (Number.isFinite(picked)) state.widthMm = picked;

    if (!note) return;
    if (moved && Number.isFinite(picked)) {
      const pieceLabel = state.pieceType && cfg.pieceLabels[state.pieceType];
      const template = pieceLabel ? cfg.copy.widthUnavailablePiece : cfg.copy.widthUnavailableProfile;
      note.textContent = String(template || '')
        .replace('{from}', String(wanted))
        .replace('{to}', String(picked))
        .replace('{piece}', pieceLabel ? pieceLabel.toLowerCase() : '');
      note.hidden = !note.textContent;
    } else {
      note.hidden = true;
      note.textContent = '';
    }
  }

  function renderGrades() {
    const grades = gaugesFor(state.pieceType, state.profile, state.widthMm, state.orientation);
    const sel = $('[data-djh-hc-grade]');
    if (!sel) return;
    sel.innerHTML = '';
    grades.forEach((g) => {
      const opt = document.createElement('option');
      opt.value = String(g.gauge);
      opt.textContent = g.label + ' · ' + g.gauge.toFixed(2) + ' mm';
      sel.appendChild(opt);
    });
    const match = grades.find((g) => g.label === state.gradeLabel) || grades[Math.min(1, grades.length - 1)];
    if (match) {
      sel.value = String(match.gauge);
      state.gaugeMm = match.gauge;
      state.gradeLabel = match.label;
    }
  }

  function renderSizeControl() {
    const braceletWrap = $('[data-djh-hc-length-wrap]');
    const ringWrap = $('[data-djh-hc-ring-wrap]');
    const hingeWrap = $('[data-djh-hc-hinge-wrap]');
    const isBracelet = state.pieceType === 'bracelet';
    const isRing = state.pieceType === 'ring';

    // A chain is a pendant's question and nobody else's.
    const engravingNote = $('[data-djh-hc-engraving]');
    if (engravingNote) engravingNote.hidden = state.pieceType !== 'pendant';

    const isPendant = state.pieceType === 'pendant';
    // A pendant has no inside: what is cut on the other face is its back engraving.
    document.querySelectorAll('[data-djh-hc-inside-legend],[data-djh-hc-inside-label]').forEach((el) => {
      el.textContent = isPendant ? cfg.copy.backWord : el.getAttribute('data-djh-hc-default');
    });
    const pendantWrap = $('[data-djh-hc-pendant-wrap]');

    if (braceletWrap) braceletWrap.hidden = !isBracelet;
    if (ringWrap) ringWrap.hidden = !isRing;
    if (pendantWrap) pendantWrap.hidden = !isPendant;
    if (hingeWrap) hingeWrap.hidden = !isBracelet;

    renderDesignFinish();

    if (isBracelet) {
      const picked = fillSelect($('[data-djh-hc-length]'), BRACELET_LENGTHS, state.lengthIn, (v) => String(v) + ' in');
      if (Number.isFinite(picked)) state.lengthIn = picked;
      const hint = $('[data-djh-hc-length-hint]');
      // A hinge changes what you measure: wrist, not the hand it has to pass over (Mike).
      if (hint) hint.textContent = state.hinge ? cfg.copy.lengthHintHinged : cfg.copy.lengthHintSolid;
    }
    if (isPendant) renderPendantLength();

    if (isRing) {
      const picked = fillSelect($('[data-djh-hc-ring]'), RING_SIZES, state.ringSize, (v) => cfg.copy.ringSizePrefix + ' ' + v);
      if (Number.isFinite(picked)) state.ringSize = picked;
    }
    if (!isBracelet) {
      state.hinge = false;
      // A hidden Open clasp would be read back on the next change and price a ring as clasped.
      const solid = form && form.querySelector('[name="hinge"][value=""]');
      if (solid) solid.checked = true;
    }
  }

  // Pendants have little room: a design and a name compete for it. Henry (2026-10-05): both may be chosen, nothing is
  // blocked, the jewelers work out the fit after the order, so this is a note and never a rule.
  function renderPendantDesignNote() {
    const el = $('[data-djh-hc-pendant-design-note]');
    if (!el) return;
    const show = state.pieceType === 'pendant' && !!state.design;
    el.hidden = !show;
    el.textContent = show ? (state.name ? cfg.copy.pendantDesignNoteName : cfg.copy.pendantDesignNote) : '';
  }

  function renderLettering() {
    renderPendantDesignNote();
    // Silver and platinum have no color to choose.
    const colorWrap = $('[data-djh-hc-color-wrap]');
    if (colorWrap) colorWrap.hidden = state.metal !== 'gold';
    const ground = GROUND_BY_FILL[state.fill] || '';
    const groundEl = $('[data-djh-hc-ground]');
    if (groundEl) groundEl.textContent = ground;
    const enamelWrap = $('[data-djh-hc-enamel-wrap]');
    if (enamelWrap) enamelWrap.hidden = !FILL_IS_ENAMEL[state.fill];
    // The raised fill names the piece's metal, so its option text has to follow the metal
    // field rather than sit where Liquid painted it. Rewriting the option keeps the control
    // and the summary saying the same words — the alternative is a dropdown reading "Raised
    // gold" on a white gold bracelet, which is the drift D-231 removed from the stored value.
    const raisedOpt = $('[data-djh-hc-fill-raised]');
    if (raisedOpt) raisedOpt.textContent = fillLabelFor('raised');

    // D-99: "Chevron belongs to the lettering, and a name gates all of it. No name, no
    // lettering fields, no chevron." A chevron is engraved WITH a name — there is nothing
    // for it to sit beside on a piece that carries none.
    //
    // NOTE THE SCOPE: this gates the CHEVRON only. D-99 says a name should gate every
    // lettering field, and today it gates none of them — measured 2026-08-29, `state.name`
    // is read from the form and printed in the summary and does nothing else. Extending
    // the gate to fill, letter style and enamel would change behaviour Henry has not asked
    // about, so it is reported rather than assumed. The wrap attribute below is the same
    // shape those fields would use.
    const chevronWrap = $('[data-djh-hc-chevron-wrap]');
    if (chevronWrap) {
      const hasName = !!state.name;
      chevronWrap.hidden = !hasName;
      // A HIDDEN SELECT STILL SUBMITS ITS VALUE. Pick a chevron, then clear the name, and
      // the quote and the order would carry a chevron for a piece with nothing to engrave
      // — the same leak closed on the Slider add-on this morning. Withdrawing the control
      // means withdrawing the answer.
      // The select keeps whatever was chosen, so a name typed again comes back to it
      // rather than silently resetting to Standard. Only the ANSWER is withdrawn.
      if (!hasName) state.chevron = '';
    }
  }

  function renderEngravingCount() {
    const el = $('[data-djh-hc-engraving-count]');
    if (!el) return;
    const n = state.inside.length;
    // Deliberately informational. Both Mike and Dianna said there is no number — the room comes
    // from wrist or finger size, and the shop judges it. So: count, never a cap, never a block.
    el.textContent = n ? cfg.copy.engravingCount.replace('{n}', String(n)) : '';
  }

  // ── Quoting ───────────────────────────────────────────────────────────────────────────────

  function money(cents) {
    const whole = cents % 100 === 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: whole ? 0 : 2,
      maximumFractionDigits: whole ? 0 : 2,
    }).format(cents / 100);
  }

  /** "14K Yellow" for gold; the metal's own name ("Sterling silver", "Platinum") otherwise. */
  function metalLabel() {
    if (state.metal === 'gold') return state.karat + 'K ' + state.metalColor;
    const sel = form && form.querySelector('[name="karat"]');
    const opt = sel && sel.options[sel.selectedIndex];
    return opt ? opt.textContent.split('·')[0].trim() : state.metal;
  }

  /**
   * The metal as a NOUN — "gold", "white gold", "sterling silver" — which is a different
   * question from metalLabel()'s "14K Yellow". D-231: the lettering fill stores `raised` and
   * carries no metal of its own, because the metal belongs to the PIECE; the label is composed
   * so a yellow band reads "Raised gold" and a white one "Raised white gold". Stating the metal
   * inside the stored value is how we ended up with `raised_gold` on a white gold bracelet.
   */
  function metalNoun() {
    if (state.metal !== 'gold') {
      const sel = form && form.querySelector('[name="karat"]');
      const opt = sel && sel.options[sel.selectedIndex];
      return (opt ? opt.textContent.split('·')[0].trim() : state.metal).toLowerCase();
    }
    const color = (state.metalColor || '').toLowerCase();
    if (!color || color === 'yellow') return cfg.copy.metalNounDefault;
    return color + ' ' + cfg.copy.metalNounDefault;
  }

  /** The fill's label, with [metal] resolved against the piece (square brackets: a { inside a
   *  Liquid output tag is a syntax error Shopify rejects and theme check does not catch). */
  function fillLabelFor(value) {
    const raw = (cfg.fills.find((f) => f.value === value) || {}).label || value;
    return raw.replace('[metal]', metalNoun());
  }

  function letteringLabel() {
    const fillLabel = fillLabelFor(state.fill);
    let out = fillLabel;
    if (FILL_IS_ENAMEL[state.fill]) {
      out += ' · ' + (state.customEnamel ? cfg.copy.customEnamelLabel : state.enamelColor);
    }
    return out;
  }

  /** Never let an enameled border reach the shop without the color it was ordered in. */
  function borderLabel() {
    if (!borderIsEnamel()) return state.border;
    const color = state.customBorderEnamel ? cfg.copy.customEnamelLabel : state.borderEnamelColor;
    return state.border + ' \u00b7 ' + color;
  }

  function engravingLabel() {
    const parts = [];
    if (state.name) parts.push(cfg.copy.namePrefix + ' ' + state.name);
    if (state.inside) parts.push((state.pieceType === 'pendant' ? cfg.copy.backPrefix : cfg.copy.insidePrefix) + ' ' + state.inside);
    return parts.join(' · ');
  }

  function payload() {
    return {
      pieceType: state.pieceType,
      // Split out of pieceType 2026-08-30. The engine reads both; sending the old
      // welded codes to the new engine, or these to the old one, fails every quote —
      // which is why this ships in one release with OPS rather than on its own.
      orientation: state.orientation,
      // THE SLUG ONLY, and deliberately not the display name as well. CATALOG looks the
      // name up from `heirloom_design`; sending both would let them disagree, and an order
      // reading "Sea Turtle" against slug `plumeria` cannot be resolved afterwards. One
      // source of truth, and it is the table. (`Design` / `_design` are the line-property
      // names on the Shopify order — a different layer from this request body.)
      design: state.design,
      designFinish: state.designFinish,
      profile: state.profile,
      widthMm: state.widthMm,
      gaugeMm: state.gaugeMm,
      karat: state.karat,
      // A PENDANT'S LENGTH IS SENT NOW. This read `pieceType === 'bracelet' ? … : null`,
      // so every pendant quote went out with no length and the engine fell back to 1.5" —
      // which read as "the engine discards pendant length" and was the theme throwing it
      // away one layer earlier. Measured against production: 1.5/1.75/2 return $805.40 /
      // $881.30 / $957.20, so the engine has always honoured it.
      //
      // `lengthIn`, never `p_length_in`: the proxy takes the first and ignores the second.
      lengthIn:
        state.pieceType === 'bracelet'
          ? state.lengthIn
          : state.pieceType === 'pendant'
            ? state.pendantLengthIn
            : null,
      ringSize: state.pieceType === 'ring' ? state.ringSize : null,
      // THE LABELLED PAIR, KEPT UNTIL OPS's ROUTE MOVES TO THE ONE WRITER. Merchant copy
      // ("Name: …", "Raised gold · Old English"), and nothing should parse it back apart.
      engraving: engravingLabel(),
      lettering: letteringLabel(),
      // THE SAME CHOICES AS SEPARATE VALUES (Integration, 2026-09-14). The one writer stores
      // each as its own field. Name and inside engraving go as typed, NFC, so an ʻokina or a
      // kahakō typed two ways is one string at the bench. Enamel color only when the fill is
      // enamel; `custom` when the customer asked for a color not on the list.
      name: state.name.normalize('NFC'),
      insideEngraving: state.inside.normalize('NFC'),
      letterStyle: state.style,
      fill: state.fill,
      enamelColor: FILL_IS_ENAMEL[state.fill] ? (state.customEnamel ? 'custom' : state.enamelColor) : null,
      // The VALUE (standard · round · plumeria flower), not the title-cased label, and null
      // with no name: D-99, a chevron is engraved with a name and there is nothing to sit beside.
      chevron: state.name ? String(state.chevron || '').trim().toLowerCase() || null : null,
      border: state.border,
      finish: GROUND_BY_FILL[state.fill] || '',
      hinge: state.hinge ? cfg.copy.hingeOpen : cfg.copy.hingeSolid,
      // SENT NOW, PRICED LATER. CATALOG's rope tables are going into Postgres and the
      // pricing function does not exist yet. These travel so that the moment it does the
      // engine has what it needs and nothing here changes — and so the inquiry that goes
      // to a person meanwhile carries the actual specification rather than "a chain".
      // Null on anything that is not a bonded-chain pendant.
      construction: state.construction || null,
      chainMm: state.chainMm,
      chainLengthIn: state.chainLengthIn,
      addons: state.addons.slice(),
      metal: metalLabel(),
      // THE FACTS THE TIER DECIDES "CAN THIS BE BOUGHT ONLINE" FROM (CATALOG 7e7b13678). Anything this request does not say is
      // routed to a person by the tier, never assumed fine, so these are sent every time and nothing here judges them.
      metalKind: state.metal,
      metalColor: state.metalColor,
      hingeOpen: Boolean(state.hinge),
      customEnamel: Boolean(state.customEnamel),
    };
  }

  function setPanel(mode, opts) {
    const o = opts || {};
    const priceEl = $('[data-djh-hc-price]');
    const noteEl = $('[data-djh-hc-price-note]');
    const inquiryBtn = $('[data-djh-hc-inquiry]');
    const warnEl = $('[data-djh-hc-warnings]');

    root.dataset.djhHcMode = mode;
    // ONE HEADLINE, EVERY STATE. It used to swap between a number and a sentence, which
    // is what made the priced and unpriced panels read as two different screens. The
    // number has its own labelled row above the button now.
    if (priceEl) priceEl.textContent = cfg.copy.priceOnRequest || '';
    const estimate = $('[data-djh-hc-estimate]');
    const estimateValue = $('[data-djh-hc-estimate-value]');
    const hasNumber = mode === 'priced' && o.price;
    if (estimate) estimate.hidden = !hasNumber;
    if (estimateValue) estimateValue.textContent = hasNumber ? o.price : '';
    if (noteEl) noteEl.textContent = o.note || '';
    // ALWAYS VISIBLE NOW, AND THIS LINE IS THE WHOLE CHANGE. It used to read
    // `inquiryBtn.hidden = mode === 'priced'` — the enquiry stood down whenever a
    // price resolved, because the buy button took over. With the purchase removed
    // that would leave a fully-priced piece with no action on the panel at all.
    if (inquiryBtn) inquiryBtn.hidden = false;
    if (warnEl) {
      warnEl.hidden = !o.warning;
      warnEl.textContent = o.warning || '';
    }
    // The bar mirrors this panel's action, so it re-reads whenever the panel moves.
    renderBar();
  }

  function constructionLabel() {
    if (state.construction === 'slider') return cfg.copy.constructionSlider || 'Slider';
    if (state.construction === 'chain') return cfg.copy.constructionChain || 'Bar and rope chain';
    return '';
  }

  // Empty unless there is a bonded chain — `.filter((r) => r[1])` in renderSummary drops
  // the row entirely, which is why an unchosen construction adds nothing to the panel.
  function chainLabel() {
    if (state.construction !== 'chain') return '';
    if (!state.chainMm || !state.chainLengthIn) return '';
    return state.chainMm + ' mm · ' + state.chainLengthIn + ' in';
  }

  function renderSummary() {
    const list = $('[data-djh-hc-summary]');
    if (!list) return;
    const rows = [
      [cfg.copy.rowPiece, (cfg.pieceLabels[state.pieceType] || state.pieceType)],
      [cfg.copy.rowMetal, metalLabel()],
      [cfg.copy.rowWidth, state.widthMm ? state.widthMm + ' mm ' + state.profile : ''],
      [cfg.copy.rowWeight, state.gradeLabel],
      [
        cfg.copy.rowSize,
        state.pieceType === 'bracelet' ? state.lengthIn + ' in'
        : state.pieceType === 'ring' ? cfg.copy.ringSizePrefix + ' ' + state.ringSize
        : state.pendantLengthIn + ' in',
      ],
      // Hinge is chosen in the Size section, so it reads directly after it. It sat last,
      // and an exception is what makes the rule invisible.
      // Closure is a bracelet's field (heirloom.construction); a ring or a pendant has none to show.
      [cfg.copy.rowHinge, state.pieceType !== 'bracelet' ? '' : state.hinge ? cfg.copy.hingeOpen : cfg.copy.hingeSolid],
      // DESIGN SITS AFTER SIZE HERE, AND ONLY HERE. Henry's order for the panel.
      //
      // THE FORM STILL ASKS FOR IT BEFORE WIDTH, deliberately: "raised carving needs
      // 10 mm or wider", so the design steers the width choice and has to come first
      // while someone is deciding. The panel is read after the fact, where the piece
      // reads better grouped by material then by decoration. The mirror between form
      // and panel is broken on purpose, in the direction that serves each.
      [cfg.copy.rowDesign, state.designName],
      // Carving is the DESIGN'S finish, so it travels with it. It used to sit five rows
      // above, between Piece and Metal, which read as a property of the metal.
      [cfg.copy.rowDesignFinish, state.design === 'none' ? '' : finishLabel()],
      [cfg.copy.rowName, state.name],
      [cfg.copy.rowLetterStyle, state.style],
      [cfg.copy.rowLettering, letteringLabel()],
      [cfg.copy.rowGround, GROUND_BY_FILL[state.fill] || ''],
      [cfg.copy.rowChevron, state.chevron],
      [cfg.copy.rowBorder, borderLabel()],
      // INSIDE ENGRAVING SITS WHERE ITS SECTION DOES, near the end. The name moved up
      // to where it is typed — a customer checking the spelling of their own name should
      // not have to find it inside a combined line.
      [state.pieceType === 'pendant' ? cfg.copy.backWord : cfg.copy.rowInside, state.inside],
      // CONSTRUCTION AND ITS CHAIN, at the end with the add-ons, because they are how
      // the piece is HUNG rather than what it is made of.
      //
      // These rows exist because the quote is routed to a person while the engine
      // cannot price a chain — and an inquiry that says "bar and rope chain" without
      // the width and length is one the shop has to write back about. The payload
      // carried the spec before this did, which made the comment beside it a claim the
      // page could not support: the customer and the shop both read THIS list.
      [cfg.copy.rowConstruction, constructionLabel()],
      [cfg.copy.rowChain, chainLabel()],
      [cfg.copy.rowAddons, state.addons.join(', ')],
    ].filter((r) => r[1]);

    // ANYTHING ELSE IS NOT A PROPERTY OF THE PIECE. Every row above is a thing being
    // made; this is a message to the shop, so it sits after the list with a rule above
    // it rather than becoming row fifteen. Truncated here and sent in full — the panel's
    // height should not depend on how much someone typed.
    const noteEl = $('[data-djh-hc-summary-note]');
    if (noteEl) {
      const note = String(state.shopNote || '');
      noteEl.hidden = !note;
      noteEl.textContent = note.length > 42 ? note.slice(0, 42) + '…' : note;
    }

    list.innerHTML = '';
    rows.forEach((r) => {
      const dt = document.createElement('dt');
      dt.textContent = r[0];
      const dd = document.createElement('dd');
      dd.textContent = r[1];
      list.appendChild(dt);
      list.appendChild(dd);
    });
  }

  async function refreshQuote() {
    // No piece, no quote. Without this the boot sequence would price a null piece
    // against the proxy on every page load through the general door — a request whose
    // answer could only ever be about a piece nobody chose.
    if (!state.pieceType) {
      quote = null;
      return;
    }
    renderSummary();
    drawScale();

    // ── A DESIGN IS REQUIRED BEFORE SELF-SERVE CHECKOUT ─────────────────────────────────
    //
    // NOT TEMPORARY ANY MORE, AND THE LABEL THAT SAID SO WAS WRONG BY 2026-08-25. This
    // began as a guard while the form had no design field at all: a customer could
    // configure a bracelet, pay a real Shopify invoice, and reach the bench with width,
    // metal, lettering, border, hinge and engraving — and nothing saying what to carve.
    // It was written to remove itself the moment payload() carried a design.
    //
    // The field landed (D-84 vocabulary, 0a3c051) and this did NOT become unnecessary. It
    // became the field's required-field enforcement: the control opens on an empty
    // placeholder, so payload().design is blank until the customer chooses, and this is
    // what stops "nobody was asked" reaching checkout. `none` — a customer choosing a
    // plain piece — satisfies it, which is the whole reason `none` and "" are different
    // answers on both sides of the wire.
    //
    // So the old header, the "delete it once the field lands" line, and the launch-revisit
    // note all described a thing this no longer is. Principle ① — a stale instruction is
    // an obsolete path with a longer blast radius than a stale function, because the
    // function fails loudly. Rewritten rather than left to be obeyed.
    //
    // WHY IT LIVES HERE AND NOT ONLY IN THE MARKUP: `required` on the select is the
    // browser's check and a crafted request never meets it. CATALOG's /order is the line
    // that actually holds against a direct post; this one keeps the page honest.
    //
    // ?from= is NOT sufficient and must not be mistaken for it. It sets state.fromProduct,
    // which reaches the inquiry text and a breadcrumb — never payload().
    //
    // ── THE NO-DESIGN GUARD WAS REMOVED 2026-08-31, AND ITS LESSON WAS NOT ──
    //
    // It returned early when no design was chosen, showing a sentence instead of a
    // price. Its comment warned of "a price shown for a configuration nobody has priced
    // — the exact silent-undercharging failure these guards exist to prevent."
    //
    // MEASURED AGAINST THE ENGINE: quote_hawaiian_piece takes NO design argument. Bamboo
    // Royal, Haku Lei and no design at all return the same number for the same metal,
    // width, gauge and length. So this branch was guarding a difference that does not
    // exist, and Henry asked for the estimate to show from the start.
    //
    // 🔴 THE CLASS OF FAILURE IS STILL REAL. `raised`, `hinge` and the chevron DO change
    // what can be priced, and the guards below still hold for them — including the
    // `seq += 1` that retires a quote already in flight, without which a request sent a
    // moment earlier paints its price over a panel that has just refused. Measured
    // 2026-08-29. Removing one instance is not removing the rule.
    //
    // Whether a hand-carved design SHOULD cost the same as an undecorated band is a
    // question for Henry, not a thing for this file to decide by refusing to quote.
    // Fails safe: routes to a person. It never blocks the inquiry route.

    // WHETHER THIS CAN BE BOUGHT ONLINE IS THE TIER'S ANSWER, NOT THIS PAGE'S (OPS audit H6). The page used to decide, in the
    // browser, which choices were "unpriced" and send those to a person with its own sentences; every quote now goes to the
    // tier and its `online` object says it (sellable, routed choices, the approved reason sentences). Nothing is judged here.
    const mine = ++seq;
    setPanel('loading', { price: cfg.copy.pricing, note: '' });

    let res;
    try {
      res = await fetch(cfg.proxyBase + '/quote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(payload()),
      });
    } catch (_e) {
      if (mine !== seq) return;
      quote = null;
      state.quoted = '';
      setPanel('inquiry', { price: cfg.copy.priceOnRequest, note: cfg.copy.offlineNote });
      return;
    }
    if (mine !== seq) return;

    if (!res.ok) {
      quote = null;
      state.quoted = '';
      // A 400 is the door refusing the request and saying why in a plain sentence (a character the workshop cannot cut, a name too
      // long); show that, and keep "offline" for a door that could not answer.
      let said = '';
      if (res.status === 400) {
        try {
          const body = await res.json();
          said = (Array.isArray(body.warnings) && body.warnings[0]) || (typeof body.error === 'string' ? body.error : '');
        } catch (_e) { said = ''; }
        if (mine !== seq) return;
      }
      setPanel('inquiry', { price: cfg.copy.priceOnRequest, note: said || cfg.copy.offlineNote });
      return;
    }

    let data;
    try {
      data = await res.json();
    } catch (_e) {
      quote = null;
      state.quoted = '';
      setPanel('inquiry', { price: cfg.copy.priceOnRequest, note: cfg.copy.offlineNote });
      return;
    }
    if (mine !== seq) return;

    quote = data;

    // THE TIER'S ANSWER (`online`): when it says this is not for online checkout, its own reason sentences are shown exactly as
    // they come (Henry's approved words, never reworded here). A reply with no `online` object is NOT assumed sellable.
    const online = data && data.online;
    // A NAME PENDANT WITH NO NAME (the tier, D-357): no price, and its own sentence says so. Shown as it comes, until a name is typed.
    if (data && data.needsName) {
      state.quoted = '';
      const sentence = Array.isArray(data.warnings) ? data.warnings.filter(Boolean).join('\n') : '';
      setPanel('inquiry', { price: cfg.copy.priceOnRequest, note: sentence || cfg.copy.unquotableNote });
      return;
    }

    if (!online || !online.sellable) {
      state.quoted = '';
      // `online.said.text` is the lead, one line per reason and the way forward, joined by line breaks; the note draws them with pre-line.
      const said = online && online.said && typeof online.said.text === 'string' ? online.said.text : '';
      const reasons = online && Array.isArray(online.reasons) ? online.reasons.filter(Boolean) : [];
      setPanel('inquiry', { price: cfg.copy.priceOnRequest, note: said || (reasons.length ? reasons.join('\n') : cfg.copy.unquotableNote) });
      return;
    }

    if (data.priceCents == null || !data.sellable) {
      state.quoted = '';
      setPanel('inquiry', { price: cfg.copy.priceOnRequest, note: cfg.copy.unquotableNote });
      return;
    }

    // THE ENQUIRY CARRIES THE NUMBER THE CUSTOMER WAS SHOWN. Without this the shop
    // reads a spec and re-quotes it, and any difference lands on a customer who
    // believes they already have a price. Held as the formatted string rather than
    // cents so what is sent is literally what was on screen.
    state.quoted = money(data.priceCents);

    setPanel('priced', {
      price: money(data.priceCents),
      note: cfg.copy.priceNote,
      // A SUSPECT CHART ROW still prices; it is advisory. Surface it plainly rather than hiding it.
      warning: (data.warnings || []).length ? cfg.copy.confirmNote : '',
    });
  }

  let debounce;
  function scheduleQuote() {
    window.clearTimeout(debounce);
    debounce = window.setTimeout(refreshQuote, 350);
  }

  // ── Actions ───────────────────────────────────────────────────────────────────────────────

  function inquiryUrl() {
    const lines = [
      cfg.copy.inquiryIntro,
      '',
    ];
    // Names the piece rather than describing it, so the bench has a reference.
    if (state.fromProduct) {
      // ⑥ THE LINK, NOT JUST THE NAME — Henry, 2026-09-08. A title is something the bench
      // has to search for; a URL is something they click. `state.fromUrl` is relative, so
      // it is made absolute here — the destination of this text is an email inbox, where a
      // relative path is not a link at all.
      lines.push(cfg.copy.fromLabel + ' ' + state.fromProduct);
      if (state.fromUrl) lines.push(window.location.origin + state.fromUrl);
      lines.push('');
    }
    lines.push(...[
      (cfg.pieceLabels[state.pieceType] || state.pieceType) + ' · ' + metalLabel(),
      // Named first among the details because it is the piece. The slug rides along in
      // brackets so the bench and the order record agree even if the display name is
      // later re-worded.
      cfg.copy.rowDesign + ': ' + (state.designName || '—') + (state.design ? ' (' + state.design + ')' : ''),
      ...(state.design === 'none' ? [] : [cfg.copy.rowDesignFinish + ': ' + finishLabel()]),
      cfg.copy.rowWidth + ': ' + state.widthMm + ' mm ' + state.profile + ' (' + state.gradeLabel + ')',
      cfg.copy.rowSize + ': ' + (
        state.pieceType === 'bracelet' ? state.lengthIn + ' in'
        : state.pieceType === 'ring' ? cfg.copy.ringSizePrefix + ' ' + state.ringSize
        : state.pendantLengthIn + ' in'
      ),
      cfg.copy.rowLetterStyle + ': ' + state.style,
      cfg.copy.rowLettering + ': ' + letteringLabel(),
      cfg.copy.rowGround + ': ' + (GROUND_BY_FILL[state.fill] || ''),
      cfg.copy.rowBorder + ': ' + borderLabel(),
      cfg.copy.rowHinge + ': ' + (state.hinge ? cfg.copy.hingeOpen : cfg.copy.hingeSolid),
    ]);
    if (engravingLabel()) lines.push(cfg.copy.rowEngraving + ': ' + engravingLabel());
    // THE CHEVRON TRAVELS WITH THE NAME (2026-09-19). It reached the on-page summary and the retired
    // order payload but never this text, so an enquiry for a Round or Plumeria flower chevron did not
    // say so — and this text is the only copy of the order the bench receives. Worded as the summary
    // row words it, and only with a name: no name, no chevron (D-99).
    if (state.name && state.chevron) lines.push(cfg.copy.rowChevron + ': ' + state.chevron);
    if (state.addons.length) lines.push(cfg.copy.rowAddons + ': ' + state.addons.join(', '));
    // Last, and only when there is one. An enquiry with no quote is a real state —
    // an unpriced combination is exactly the case that most needs a person — and
    // printing "Price: —" would invite someone to read a dash as a number.
    if (state.quoted) lines.push('', cfg.copy.rowQuoted + ': ' + state.quoted);
    // IN FULL HERE, truncated only in the panel. The shop should read what was written;
    // it is the panel's height that cannot afford the whole thing.
    if (state.shopNote) lines.push('', cfg.copy.rowShopNote + ': ' + state.shopNote);
    // ⑥ `from` AND `variant` RIDE ALONG so the inquiry page can draw the same reference
    // card this page draws. They are the two parameters the PDP set in the first place —
    // carried, not re-derived.
    var url = cfg.inquiryUrl + '?djh_details=' + encodeURIComponent(lines.join('\n'));
    var q = new URLSearchParams(window.location.search);
    var fromHandle = q.get('from');
    if (fromHandle) {
      url += '&from=' + encodeURIComponent(fromHandle);
      if (q.get('variant')) url += '&variant=' + encodeURIComponent(q.get('variant'));
    }
    return url;
  }

  // `order()` AND ITS CHECKOUT PATH WERE REMOVED 2026-08-30 (Henry).
  //
  // It POSTed to {proxy}/order and sent the customer to `data.invoiceUrl` — Shopify
  // checkout. The button never bought them speed: the page promises 7–14 working days
  // and a signed design draft before anything is cut, so paying at midnight delivered
  // no sooner. It only let them pay earlier, which serves cash flow and not them.
  //
  // Against that, a name cut into gold cannot be un-made. Three of the seventeen
  // inputs are where that goes wrong — spelling (Kuulei / Ku'ulei / Kuʻulei are three
  // different pieces), size (a name pendant is sized by the name, not a tape), and
  // combinations that are valid but wrong for the piece.
  //
  // The human checkpoint already existed — "we check every name with you" and "you
  // approve and sign a design draft" — it was simply on the far side of the money.
  // This puts the money on the far side of it.
  //
  // THE PRICING STAYS. A real number from real gold weight, shown before anyone has
  // to ask, is what earns the enquiry. The price built the trust; the button spent it.

  // ── Wiring ────────────────────────────────────────────────────────────────────────────────

  function readForm() {
    const d = new FormData(form);
    state.pieceType = d.get('piece_type') || null;
    if (state.pieceType === 'pendant') {
      state.orientation = String(d.get('orientation') || 'vertical').trim();
      state.construction = String(d.get('construction') || '').trim();
    } else {
      state.orientation = 'not applicable';
    }
    // READ HERE, NOT ONLY IN onChange'S else BRANCH. The pendant length was captured in
    // that branch alone, so any caller that ran readForm() and then re-rendered — the
    // override handler did exactly this — rendered from the PREVIOUS value and wrote it
    // back over the customer's choice. The form is the source; readForm should read it.
    if (state.pieceType === 'pendant') {
      const pendantLength = parseFloat(d.get('pendant_length'));
      if (Number.isFinite(pendantLength)) state.pendantLengthIn = pendantLength;
    }
    // A NOTE TO THE SHOP. Read into state and carried to the ENQUIRY only. It is
    // deliberately absent from payload(): the quote must not see free text, and the
    // engine refuses add-ons it cannot price — a note is not an add-on.
    state.shopNote = String(d.get('shop_note') || '').trim();
    state.design = d.get('design') || null;
    state.designFinish = d.get('design_finish') || state.designFinish;
    // The display name is read off the chosen <option> rather than mapped in JS, so the
    // two can never disagree — the snippet that renders the name is the same one that
    // carries the slug.
    const designEl = form.querySelector('[data-djh-hc-design]');
    const chosen = designEl && designEl.selectedIndex >= 0 ? designEl.options[designEl.selectedIndex] : null;
    state.designName = state.design && chosen ? chosen.textContent.trim() : '';
    state.profile = d.get('profile') || state.profile;
    // Gold carries its karat as a number; silver and platinum are words, and are a metal of their own.
    const metalChoice = String(d.get('karat') || '');
    if (/^\d+$/.test(metalChoice)) {
      state.karat = parseInt(metalChoice, 10);
      state.metal = 'gold';
    } else if (metalChoice) {
      state.metal = metalChoice;
    }
    state.metalColor = d.get('metal_color') || state.metalColor;
    state.fill = d.get('fill') || state.fill;
    state.style = d.get('letter_style') || state.style;
    state.chevron = String(d.get('chevron') || '').trim() || state.chevron;
    state.border = d.get('border') || state.border;
    state.name = String(d.get('name') || '').trim();
    state.inside = String(d.get('inside') || '');
    state.hinge = d.get('hinge') === 'on';
    const enamel = d.get('enamel_color');
    state.customEnamel = enamel === '__custom__';
    if (enamel && !state.customEnamel) state.enamelColor = enamel;
    const borderEnamel = d.get('border_enamel_color');
    state.customBorderEnamel = borderEnamel === '__custom__';
    if (borderEnamel && !state.customBorderEnamel) state.borderEnamelColor = borderEnamel;
    state.addons = d.getAll('addons').map(String);
  }

  function onChange(e) {
    const t = e.target;
    // Once the customer picks an edge color themselves, it stops following the lettering.
    if (t && t.name === 'border_enamel_color') state.borderEnamelTouched = true;
    readForm();

    // Structural changes rebuild the dependent lists; everything else just re-quotes.
    if (t && (t.name === 'piece_type' || t.name === 'profile')) {
      gatePieceType();
      renderWidths();
      renderGrades();
      renderSizeControl();
    } else if (t && t.name === 'hinge') {
      renderSizeControl();
    } else {
      const w = $('[data-djh-hc-width]');
      const g = $('[data-djh-hc-grade]');
      const l = $('[data-djh-hc-length]');
      const r = $('[data-djh-hc-ring]');
      if (w && w.value) state.widthMm = parseFloat(w.value);
      // The customer picking a width replaces any width we moved for them.
      if (t && t.name === 'width') {
        // The named gauges are per width in the chart: a new width redraws them.
        renderGrades();
        const widthNote = $('[data-djh-hc-width-note]');
        if (widthNote) {
          widthNote.hidden = true;
          widthNote.textContent = '';
        }
      }
      if (g && g.value) {
        state.gaugeMm = parseFloat(g.value);
        const opt = g.options[g.selectedIndex];
        state.gradeLabel = ((opt && opt.textContent) || '').split(' · ')[0];
      }
      if (l && l.value && state.pieceType === 'bracelet') state.lengthIn = parseFloat(l.value);
      const pl = $('[data-djh-hc-pendant-length]');
      if (pl && pl.value && state.pieceType === 'pendant') state.pendantLengthIn = parseFloat(pl.value);
      if (r && r.value && state.pieceType === 'ring') state.ringSize = parseFloat(r.value);
    }

    renderConstruction();
    renderLettering();
    renderBorder();
    // Beside renderBorder because it is the same kind of rule: a width-dependent option.
    // renderSizeControl() alone is not enough — that runs on piece and profile changes, and
    // the width select changes through the branch above without touching it.
    renderDesignFinish();
    renderSizingLink();
    renderEngravingCount();
    renderBar();
    scheduleQuote();
  }

  if (form) {
    // A DELIBERATE OVERRIDE STOPS THE RECOMMENDATION STEERING. Without this, typing one
    // more letter would silently drag the length back to the recommended one and undo a
    // choice the customer had already made — which is the opposite of the flexibility
    // this picker exists to give.
    form.addEventListener('change', (e) => {
      if (e.target && e.target.matches('[data-djh-hc-pendant-length]')) {
        state.nameStopped = false;
        // AND RE-RENDER, or the hint keeps describing the previous choice. Overriding
        // downward is exactly when the guidance line has something to say, and it was
        // the one moment it did not update.
        readForm();
        renderPendantLength();
      }
    });
    form.addEventListener('change', onChange);
    // AFTER onChange, deliberately: onChange runs readForm(), and writeUrl reads
    // state.gradeLabel, which only readForm has just set. Registered second, so it runs
    // second — the ordering is the contract.
    form.addEventListener('change', writeUrl);
    form.addEventListener('input', (e) => {
      // `shop_note` joins the free-text fields here rather than riding on onChange,
      // which only schedules a quote — and the note deliberately does not affect the
      // quote, so nothing would have re-rendered the summary that echoes it.
      if (
        e.target &&
        (e.target.name === 'name' || e.target.name === 'inside' || e.target.name === 'shop_note')
      ) {
        if (e.target.name === 'name') {
          keepNameEngravable(e.target);
          holdNameToLength(e.target);
        }
        readForm();
        renderEngravingCount();
        // THE NAME IS HELD TO THE CHOSEN LENGTH, so typing re-renders the counter and the stop sentence.
        if (state.pieceType === 'pendant') renderPendantLength();
        renderSummary();
      }
    });
    form.addEventListener('submit', (e) => e.preventDefault());
  }

  // The stop sentence's button moves the length up one step; the name stays as typed and the counter and the quote follow.
  const longerBtn = $('[data-djh-hc-pendant-longer]');
  if (longerBtn) {
    longerBtn.addEventListener('click', () => {
      const next = parseFloat(longerBtn.dataset.djhHcNextLength);
      if (!Number.isFinite(next)) return;
      state.pendantLengthIn = next;
      state.nameStopped = false;
      renderPendantLength();
      renderSummary();
      scheduleQuote();
    });
  }

  // ʻOkina and kahakō must be typeable, not just renderable (B-96). Most keyboards cannot
  // produce them, and a name engraved without its ʻokina is a different word.
  // ONE ROW THAT FOLLOWS THE FOCUSED FIELD. It used to be two rows, each hard-bound to
  // its own field by `data-djh-hc-char-for`. Now the row is moved next to whichever
  // char-target field has focus, and the buttons write into that one.
  //
  // AUTHORED UNDER THE NAME AND ONLY MOVED, never created — with no JS it is present and
  // working for the field where a Hawaiian name is most likely to be typed.
  var djhCharTarget = root.querySelector('[data-djh-hc-char-target="name"]');
  var djhCharRow = root.querySelector('[data-djh-hc-chars]');
  root.querySelectorAll('[data-djh-hc-char-target]').forEach((field) => {
    field.addEventListener('focus', () => {
      djhCharTarget = field;
      // Placed straight after the field so it is adjacent at any scroll position. The
      // focused element is not the one moving, so focus is not disturbed.
      if (djhCharRow && field.nextSibling !== djhCharRow) {
        field.parentNode.insertBefore(djhCharRow, field.nextSibling);
      }
    });
  });

  root.querySelectorAll('[data-djh-hc-char]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const target = djhCharTarget;
      if (!target) return;
      const ch = btn.dataset.djhHcChar;
      const start = target.selectionStart || 0;
      const end = target.selectionEnd || 0;
      target.value = target.value.slice(0, start) + ch + target.value.slice(end);
      target.focus();
      target.setSelectionRange(start + ch.length, start + ch.length);
      if (target.name === 'name') {
        keepNameEngravable(target);
        holdNameToLength(target);
      }
      readForm();
      if (target.name === 'name' && state.pieceType === 'pendant') renderPendantLength();
      renderEngravingCount();
      renderSummary();
    });
  });

  const cal = $('[data-djh-hc-cal]');
  if (cal) {
    cal.value = String(mm.pxPerMm());
    cal.addEventListener('input', () => mm.set(cal.value));
  }
  const calReset = $('[data-djh-hc-cal-reset]');
  if (calReset) {
    calReset.addEventListener('click', () => {
      mm.reset();
      if (cal) cal.value = String(mm.CSS_PX_PER_MM);
    });
  }
  mm.onChange(drawScale);

  const inquiry = $('[data-djh-hc-inquiry]');
  if (inquiry) {
    inquiry.addEventListener('click', (e) => {
      e.preventDefault();
      window.location.assign(inquiryUrl());
    });
  }

  // ── Boot ──────────────────────────────────────────────────────────────────────────────────

  /**
   * Arrive from the thickness browse with the decision already made.
   *
   * Mike's IA is piece → thickness → customise, so by the time someone reaches this page they
   * have chosen a width by looking at it. Landing them on a default 12 mm would throw that away.
   * Only the three structural axes are honoured; anything else is a choice to make here.
   */
  /**
   * The piece they were looking at, carried across from a product page.
   *
   * Henry: the custom-version action should "copy where you are at", and it did not —
   * the PDP button went to a generic page carrying nothing. The piece type answers
   * step one; THIS answers a question the bench would otherwise have to ask, because
   * "they were looking at the 10 mm Plumeria cut-out" is a reference rather than a
   * description.
   *
   * The title is fetched rather than passed in the URL: a handle is short and stable,
   * a title is neither, and a URL a customer might send to a spouse should not carry
   * a hundred characters of product name. Fetch failure is silent — the banner simply
   * does not appear, and nothing about the form depends on it.
   *
   * D-74: this is a POINTER, never a new SKU. Product plus variant identifies the
   * piece; what this customer wants done to it is line-item properties. Minting a SKU
   * per custom order would turn 55 products into 5,000 and end the catalogue.
   */
  async function seedFromProduct() {
    const q = new URLSearchParams(window.location.search);
    const handle = q.get('from');
    const el = $('[data-djh-hc-from]');
    if (!handle || !el || !/^[a-z0-9][a-z0-9-]{0,120}$/.test(handle)) return;

    let product;
    try {
      const res = await fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' });
      if (!res.ok) return;
      product = await res.json();
    } catch (_e) {
      return;
    }
    if (!product || !product.title) return;

    const variantId = q.get('variant');
    if (variantId && window.djhVariantLookup) await window.djhVariantLookup.complete(handle, product, [variantId]);
    const variant = variantId && product.variants
      ? product.variants.find((v) => String(v.id) === String(variantId))
      : null;
    const name = variant && variant.title && variant.title !== 'Default Title'
      ? product.title + ' · ' + variant.title
      : product.title;

    state.fromProduct = name;
    state.fromUrl = '/products/' + product.handle + (variantId ? '?variant=' + encodeURIComponent(variantId) : '');

    // ⑥ A PICTURE, NOT ONLY A NAME — Henry, 2026-09-08: *"include a picture of the product
    // on the page as a reference."* Same card as the contact and inquiry pages, built here
    // rather than by djh-piece-ref.js because this page has already fetched the product and
    // a second fetch for the same JSON would be waste.
    el.hidden = false;
    el.textContent = '';
    el.className = 'djh-hc__from djh-piece-ref';

    if (product.featured_image) {
      const img = document.createElement('img');
      img.className = 'djh-piece-ref__img';
      img.src = product.featured_image;
      img.alt = '';
      img.width = 56;
      img.height = 56;
      img.loading = 'lazy';
      el.append(img);
    }

    const body = document.createElement('div');
    body.className = 'djh-piece-ref__body';
    const label = document.createElement('p');
    label.className = 'djh-piece-ref__label';
    label.textContent = cfg.copy.fromLabel;
    const a = document.createElement('a');
    a.className = 'djh-piece-ref__link';
    a.href = state.fromUrl;
    a.textContent = name;
    body.append(label, a);
    el.append(body);
    renderSummary();
  }

  /**
   * THE URL IS THE CONFIGURATION, and it is written as CHOICES rather than as a blob.
   *
   * The shop will use this before a customer does: a link is what a member of staff sends
   * after a conversation at the counter — "here is the one we talked about." So
   * `?piece=bracelet&width=8&karat=14` has to still open that bracelet after a theme
   * deploy. An opaque blob or an index into an array would both break the week something
   * is reordered, and the link would then open the WRONG piece rather than fail — worse
   * than losing state on reload, which is the thing this exists to fix.
   *
   * 🔴 THE ENGRAVED NAME IS NEVER IN THE URL. `name`, `inside` and `shop_note` are free
   * text a person typed about themselves or someone they love. A URL is shared, logged,
   * pasted into chat and kept in history — personal data does not go in one. They stay in
   * the form and reach the shop through the enquiry, as they already do.
   *
   * ⚠️ `replaceState`, NOT `pushState`, AND THAT IS A CHOICE. Pushing would give a back
   * button that steps through every dropdown — press Back twelve times and you are still
   * on the configurator, which reads as a page that will not let you leave. Replacing
   * keeps Back meaning "the page I came from" while still giving reload-survival and a
   * shareable link, which is what a customer actually loses today.
   */
  const URL_PRIVATE = ['name', 'inside', 'shop_note'];

  // The form calls it piece_type; every link the theme has ever written says ?piece=.
  // Written under the name the READ side already understands, so old links and new ones
  // stay one contract rather than two.
  const URL_ALIAS = { piece_type: 'piece' };

  function writeUrl() {
    if (!form || !window.history || typeof history.replaceState !== 'function') return;

    const q = new URLSearchParams();
    // Preserved rather than rebuilt: seedFromProduct owns these and this function does
    // not get to drop them.
    const current = new URLSearchParams(window.location.search);
    ['from', 'variant'].forEach((k) => {
      const v = current.get(k);
      if (v) q.set(k, v);
    });

    // ⚠️ ONLY WHAT THE CUSTOMER CAN SEE. A bracelet's form still holds the pendant
    // fieldset, so FormData hands over orientation, chain_mm and chain_length_in for a
    // piece that has no chain — 308 characters of URL, a third of it about a pendant.
    // Harmless on read (readForm ignores them off a pendant) and wrong for the thing this
    // is FOR: a link a salesperson reads before sending it.
    //
    // `checkVisibility()` rather than a box measurement, which is the lesson of the
    // `content-visibility` false positive earlier: a hidden control still reports real
    // numbers from getBoundingClientRect. DEFAULTS TO INCLUDING — an unsupported browser
    // or an ambiguous answer keeps the field, because a longer URL is a blemish and a
    // dropped choice is a wrong piece.
    const visible = (key) => {
      const els = form.querySelectorAll('[name="' + key + '"]');
      if (!els.length) return true;
      return Array.prototype.some.call(els, (el) => {
        if (typeof el.checkVisibility !== 'function') return true;
        return el.checkVisibility({ checkOpacity: false, checkVisibilityCSS: true });
      });
    };

    const d = new FormData(form);
    d.forEach((value, key) => {
      if (URL_PRIVATE.indexOf(key) !== -1) return;
      if (!visible(key)) return;
      // ⚠️ GRADE IS WRITTEN AS ITS LABEL, NOT ITS VALUE. The select's values are gauges in
      // millimeters while the read side matches on the LABEL (Light/Medium/Heavy), because
      // `djh.weight` speaks in labels. Writing the raw value would produce a link that
      // looks complete, round-trips nothing, and falls back to the default — which is
      // exactly how the earlier ?grade= bug hid: the default agreed with the URL by
      // accident.
      if (key === 'grade') return;
      const v = String(value).trim();
      if (!v) return;
      q.append(URL_ALIAS[key] || key, v);
    });

    if (state.gradeLabel) q.set('grade', state.gradeLabel);

    const qs = q.toString();
    history.replaceState(null, '', qs ? window.location.pathname + '?' + qs : window.location.pathname);
  }

  function seedFromUrl() {
    const q = new URLSearchParams(window.location.search);
    const piece = q.get('piece');
    const profile = q.get('profile');
    const width = parseFloat(q.get('width'));

    // A ?piece= we do not recognise FALLS BACK TO ASKING rather than to a bracelet.
    // cfg.pieceLabels is the whitelist, so a typo, a stale link, or a piece we stop
    // offering all land on the same honest state: the question, unanswered.
    if (piece && cfg.pieceLabels[piece]) {
      const el = form.querySelector('[name="piece_type"][value="' + piece + '"]');
      if (el) el.checked = true;
    }

    // ?orientation=vertical|horizontal, for a pendant.
    //
    // THE LINKS THAT USED TO SAY ?piece=pendant_vertical NOW SAY
    // ?piece=pendant&orientation=vertical — collection.heirloom-pendants.json,
    // page.guide-sizing.json and product.liquid were all updated in the same commit.
    // An OLD bookmarked link still lands on the configurator; `pendant_vertical` is no
    // longer in cfg.pieceLabels, so it falls through to the question, unanswered, which
    // is the documented behaviour for any ?piece= we do not recognise. Preselecting a
    // pendant from a stale link and GUESSING the orientation would be the worse answer.
    const orientation = q.get('orientation');
    if (orientation && cfg.orientationLabels && orientation in cfg.orientationLabels) {
      const oe = form.querySelector('[name="orientation"][value="' + orientation + '"]');
      if (oe) oe.checked = true;
    }

    // ?design=<slug> — how the browse grid hands a chosen design across.
    //
    // VALIDATED AGAINST THE RENDERED OPTIONS, not against a list in this file. If the slug
    // is not one the snippet rendered, the control simply stays unchosen and the customer
    // picks — a stale link, a typo or a design we stopped offering all land on the same
    // honest state, the same way an unrecognised ?piece= falls back to asking. Setting a
    // value the <select> does not hold would leave the form looking answered and submit
    // nothing, which is the failure this whole field exists to end.
    const designSlug = q.get('design');
    if (designSlug) {
      const sel = form.querySelector('[data-djh-hc-design]');
      const match = sel && sel.querySelector('option[value="' + CSS.escape(designSlug) + '"]');
      if (match) sel.value = designSlug;
    }
    if (profile && (profile === 'barrel' || profile === 'flat')) {
      const el = form.querySelector('[name="profile"][value="' + profile + '"]');
      if (el) el.checked = true;
    }
    // ── everything the PDP now hands across ────────────────────────────────────────
    //
    // VALIDATED AGAINST THE RENDERED CONTROL, never forced. Same rule `?piece=` and
    // `?design=` already follow: a value the control does not offer leaves it unset and
    // the customer answers it. Setting something the <select> does not hold would leave
    // the form looking answered and submit nothing.
    //
    // Three degrees of match, in order, because the product's vocabulary and the form's
    // are not the same vocabulary and neither is wrong:
    //
    //   1. the option's value exactly            border "Cut Out"  -> "Cut Out"
    //   2. the option's value, case-insensitive  metal "yellow"    -> "Yellow"
    //   3. the option's LABEL up to its first ·  weight "medium"   -> "Medium · 1.50 mm"
    //
    // (3) is not a fuzzy match: `djh.weight` holds Light/Medium/Heavy, which is exactly
    // what the labels say, while the option VALUES are gauges in millimeters. Without it
    // the single most useful attribute on a band would silently never land.
    //
    // What legitimately does NOT match still does not match: `heirloom.design_finish`
    // holds "None" and this form's control offers sand/raised, so it is ignored and the
    // customer chooses — which is the honest state, not a failure.
    function seedControl(param, controlName) {
      const raw = q.get(param);
      if (!raw) return false;
      const wanted = String(raw).trim();
      const lower = wanted.toLowerCase();

      const sel = form.querySelector('select[name="' + controlName + '"]');
      if (sel) {
        const opts = Array.prototype.slice.call(sel.options);
        const hit =
          opts.filter((o) => o.value === wanted)[0] ||
          opts.filter((o) => o.value.toLowerCase() === lower)[0] ||
          opts.filter((o) => o.textContent.split('·')[0].trim().toLowerCase() === lower)[0];
        if (!hit || hit.value === '') return false;
        sel.value = hit.value;
        return true;
      }

      const radios = Array.prototype.slice.call(
        form.querySelectorAll('[name="' + controlName + '"]')
      );
      if (!radios.length) return false;
      const rhit =
        radios.filter((r) => r.value === wanted)[0] ||
        radios.filter((r) => String(r.value).toLowerCase() === lower)[0];
      if (!rhit) return false;
      rhit.checked = true;
      return true;
    }

    // The name arrives from a product page link only as the product's own recorded name, so the
    // text field is seeded here. writeUrl() still never writes it back (URL_PRIVATE).
    const seedName = (q.get('name') || '').trim();
    const nameField = form.querySelector('input[name="name"]');
    if (seedName && nameField && !nameField.value) {
      nameField.value = seedName.slice(0, Number(nameField.maxLength) > 0 ? Number(nameField.maxLength) : 20);
    }

    seedControl('karat', 'karat');
    seedControl('metal_color', 'metal_color');
    seedControl('border', 'border');
    seedControl('design_finish', 'design_finish');

    // ── the rest of what writeUrl() now emits ────────────────────────────────────
    //
    // All of these exist in the MARKUP at seed time — checked, not assumed, because the
    // three that do not (width, length, grade) are exactly the ones that silently failed
    // for the previous author. seedControl refuses anything the control does not offer,
    // so a stale link leaves the question unanswered rather than forcing a wrong value.
    ['fill', 'letter_style', 'chevron', 'enamel_color', 'construction', 'chain_mm', 'chain_length_in', 'border_enamel_color', 'hinge']
      .forEach((n) => seedControl(n, n));

    // CHECKBOXES ARE NOT RADIOS, and seedControl cannot do them: it sets `.checked = true`
    // on the first value match, which is right for a radio group and wrong for a set. An
    // add-on list needs several boxes ticked and the rest left alone, and `hinge` needs to
    // stay OFF when the link does not mention it — which is what makes absence meaningful
    // rather than merely silent.
    ['addons'].forEach((n) => {
      const wanted = q.getAll(n).map((v) => String(v).trim());
      if (!wanted.length) return;
      form.querySelectorAll('input[type="checkbox"][name="' + n + '"]').forEach((box) => {
        if (wanted.indexOf(String(box.value).trim()) !== -1) box.checked = true;
      });
    });
    // LENGTH AND RING SIZE ARE SEEDED THROUGH STATE, NOT THROUGH THE CONTROL.
    //
    // Both <select>s are EMPTY in the markup — renderSizeControl fills them later via
    // fillSelect, which takes the current state value as the one to select. So at the
    // moment seedFromUrl runs there is no option to match and seedControl correctly
    // refuses to set anything. It was not wrong; it was early.
    //
    // I found this by driving the real link and reading the form, not by reading this
    // file: `?length=7` arrived and the select showed 7.5, the default. A check that
    // asked "is the control non-empty" called that a success, which is how a default
    // passes for a seeded value.
    //
    // Validated against the same list fillSelect uses, so an out-of-range length from a
    // stale link still falls through to the default rather than being forced in.
    // GRADE IS THE SAME STORY AS LENGTH, and it caught me the same way. Its <select>
    // is empty in the markup too — renderGrades builds the options and picks the one
    // whose LABEL matches state.gradeLabel. So `seedControl('grade', …)` could never
    // have worked, and the reason I did not notice is that the product I tested sends
    // `medium`, which is also the default: the field agreed with the URL by accident.
    // A hand-built ?grade=heavy is what exposed it.
    //
    // Matched on the label against the chart's own named gauges, because `djh.weight` speaks
    // Light/Medium/Heavy while the option values are gauges in millimeters.
    const gradeParam = q.get('grade');
    if (gradeParam) {
      const allGrades = CHOICES.pieces.reduce((acc, piece) => acc.concat(...piece.profiles.map((pr) => [].concat(...pr.widths.map((w) => (w.gauges || []).map((g) => ({ label: g.label })))))), []);
      const gradeHit = allGrades.filter(
        (g) => String(g.label).toLowerCase() === String(gradeParam).trim().toLowerCase()
      )[0];
      if (gradeHit) state.gradeLabel = gradeHit.label;
    }

    // A pendant sold as Width x Length arrives with the length it was looking at; the pendant picker keeps it when the name allows it.
    const pendantLengthParam = parseFloat(q.get('pendant_length'));
    if (Number.isFinite(pendantLengthParam) && PENDANT_RULE.some((r) => r.inches === pendantLengthParam)) {
      state.pendantLengthIn = pendantLengthParam;
    }
    const lengthParam = parseFloat(q.get('length'));
    if (Number.isFinite(lengthParam) && BRACELET_LENGTHS.indexOf(lengthParam) !== -1) {
      state.lengthIn = lengthParam;
    }
    const ringParam = parseFloat(q.get('ring_size'));
    if (Number.isFinite(ringParam) && RING_SIZES.indexOf(ringParam) !== -1) {
      state.ringSize = ringParam;
    }

    if (Number.isFinite(width)) {
      state.widthMm = width;
      // NO seedControl FOR WIDTH EITHER. renderWidths fills this <select> from
      // state.widthMm, which the line above has already set — so the state assignment
      // IS the seeding, and a seedControl call here would be a no-op that reads like a
      // guard. Three of these selects are built by JS; only karat, metal_color and
      // border exist in the markup at seed time.
      // Pacific Mill's barrel chart stops at 20 mm; flat runs to 100. If the ladder sent a width
      // the default profile cannot carry, start on the profile that can — otherwise a shopper who
      // just chose 32 mm by eye would land on "we'll confirm this one" for no reason they can see.
      if (!profile && state.pieceType && availableWidths(state.pieceType, 'barrel').indexOf(width) === -1 && availableWidths(state.pieceType, 'flat').indexOf(width) !== -1) {
        const el = form.querySelector('[name="profile"][value="flat"]');
        if (el) el.checked = true;
      }
    }
  }

  /**
   * The progressive gate.
   *
   * Set from JS, never from the markup: if this script fails to load, the attribute is
   * never added, the CSS rule never matches, and the customer gets the entire form the
   * way it has always worked. A gate written into the HTML would instead leave them
   * with one question and no way past it.
   */
  // ── the mobile sticky bar ─────────────────────────────────────────────────────────────
  //
  // NAMES THE NEXT GAP RATHER THAN COUNTING PROGRESS. Most of this form carries
  // defaults, so a count would include steps nobody needs to touch and read as
  // unfinished work a customer cannot finish — and the lettering step is conditional on
  // a name being typed, so the denominator would move as they fill it in.
  //
  // THE REQUIRED SET IS DERIVED, NOT LISTED. Unanswered means: a VISIBLE radio group
  // with nothing checked, or a VISIBLE select with an empty value. Today that is
  // piece -> design -> size; tomorrow it is whatever the markup says, which is the
  // point. A list in here would be a second source that disagrees with the fieldsets
  // the first time someone adds a step.
  function nextUnanswered() {
    if (!form) return null;
    var fieldsets = form.querySelectorAll('fieldset');
    for (var i = 0; i < fieldsets.length; i += 1) {
      var fs = fieldsets[i];
      if (fs.offsetParent === null) continue; // a hidden step is not yet owed

      var radios = fs.querySelectorAll('input[type="radio"]');
      var seen = {};
      for (var r = 0; r < radios.length; r += 1) {
        var nm = radios[r].name;
        if (!nm || seen[nm]) continue;
        seen[nm] = true;
        if (radios[r].offsetParent === null) continue;
        if (!form.querySelector('[name="' + nm + '"]:checked')) {
          return { fieldset: fs, control: radios[r] };
        }
      }

      var sels = fs.querySelectorAll('select');
      for (var q = 0; q < sels.length; q += 1) {
        if (sels[q].offsetParent === null) continue;
        // 0 options means a control the script has not populated yet — not an answer
        // the customer has withheld, so it is not their gap to close.
        if (sels[q].options.length === 0) continue;
        if (!String(sels[q].value || '').trim()) {
          return { fieldset: fs, control: sels[q] };
        }
      }
    }
    return null;
  }

  // One action to mirror now, not two. Kept as a function rather than inlined
  // because the sticky bar delegates to whatever this returns — one implementation
  // of sending, two places to press it — and that is still the right shape.
  function panelAction() {
    var inq = $('[data-djh-hc-inquiry]');
    if (inq && !inq.hidden) return inq;
    return null;
  }

  function renderBar() {
    var bar = $('[data-djh-hc-submitbar]');
    var btn = $('[data-djh-hc-submitbar-btn]');
    if (!bar || !btn) return;

    var gap = nextUnanswered();
    if (gap) {
      var prompt = gap.fieldset.getAttribute('data-djh-hc-prompt');
      btn.textContent = prompt || cfg.promptFallback || 'One more choice to continue';
      btn.setAttribute('data-state', 'incomplete');
      // NEVER A DEAD END: naming a gap it cannot reach would be a scold.
      btn.onclick = function () {
        gap.fieldset.scrollIntoView({ block: 'center', behavior: 'smooth' });
        try { gap.control.focus({ preventScroll: true }); } catch (_e) {}
      };
      bar.hidden = false;
      return;
    }

    // Nothing missing — MIRROR the panel's own action and click it, rather than
    // reimplementing sending. One implementation, two places to press it.
    var action = panelAction();
    if (!action) { bar.hidden = true; return; }
    btn.textContent = (action.textContent || '').trim();
    btn.setAttribute('data-state', 'complete');
    btn.onclick = function () { action.click(); };
    bar.hidden = false;
  }

  // HOW IT HANGS. Pendant only, and the slider half is horizontal only.
  //
  // HIDE AND CLEAR, NEVER HIDE ALONE. A hidden radio still submits, and this file has
  // already paid for that lesson once on the Slider add-on: tick it, change the piece,
  // and the inquiry carried a choice the form had stopped showing. So every branch
  // below that hides a control also unsets the state behind it.
  function renderConstruction() {
    const wrap = $('[data-djh-hc-construction-wrap]');
    if (!wrap) return;
    const isPendant = state.pieceType === 'pendant';

    // 🔴 HORIZONTAL ONLY — Henry, 2026-09-08: *"How it hangs only applies to horizontal
    // pendants. It shouldn't be an option for vertical pendants."*
    //
    // Half of this was already here: the slider was hidden on a vertical because a slider
    // is a hook on the BACK of a horizontal piece. What was left was a step headed "How it
    // hangs" offering exactly one answer — a question with no choice in it, which reads as
    // a form that has not finished loading.
    //
    // ⚠️ CHECKED AGAINST THE CATALOGUE BEFORE HIDING IT, because hiding a control that a
    // real product needs would have reintroduced the Monstera chain defect one orientation
    // over. All eight live heirloom pendants: the only two carrying `chain_mm` are the two
    // HORIZONTAL ones; the vertical and the five emblems have no chain. So nothing that
    // exists needs this control anywhere else.
    const hangsApplies = isPendant && state.orientation === 'horizontal';
    wrap.hidden = !hangsApplies;

    const sliderLabel = $('[data-djh-hc-construction-slider]');
    if (sliderLabel) sliderLabel.hidden = !hangsApplies;

    // HIDE AND CLEAR, NEVER HIDE ALONE — a hidden radio still submits, and this file has
    // paid for that once already. One branch now instead of two, because there is one
    // condition.
    if (!hangsApplies && state.construction) {
      const checked = form && form.querySelector('[name="construction"]:checked');
      if (checked) checked.checked = false;
      state.construction = '';
    }

    // A BONDED CHAIN MAKES "Chain not included." A LIE, and it sat directly beneath the
    // chain the customer had just chosen — the Monstera defect, reproduced on this page
    // by the sentence being written down twice. Same two locale strings as the product
    // page now drive both.
    //
    // SET HERE AND NOT IN renderSizeControl, which was where it lived and would have
    // been wrong: that runs on piece, profile and hinge changes only, so picking the
    // construction radio — the one input this line depends on — would not have
    // refreshed it. This function runs on every change and at boot.
    const chainNote = $('[data-djh-hc-chain]');
    if (chainNote) {
      chainNote.hidden = !isPendant;
      chainNote.textContent = state.construction === 'chain'
        ? (cfg.copy.chainIncluded || '')
        : (cfg.copy.chainNotIncluded || '');
    }

    const specWrap = $('[data-djh-hc-chain-spec-wrap]');
    const hint = $('[data-djh-hc-chain-hint]');
    const wantsChain = isPendant && state.construction === 'chain';
    if (specWrap) specWrap.hidden = !wantsChain;
    if (hint) hint.hidden = !wantsChain;

    if (wantsChain) {
      const mm = $('[data-djh-hc-chain-mm]');
      const len = $('[data-djh-hc-chain-length]');
      state.chainMm = mm && mm.value ? parseFloat(mm.value) : null;
      state.chainLengthIn = len && len.value ? parseFloat(len.value) : null;
    } else {
      state.chainMm = null;
      state.chainLengthIn = null;
    }
  }

  function gatePieceType() {
    if (!form) return;
    const chosen = form.querySelector('[name="piece_type"]:checked');
    if (chosen) form.removeAttribute('data-djh-hc-unanswered');
    else form.setAttribute('data-djh-hc-unanswered', '');

    // ADD-ONS ARE A PENDANT QUESTION. A slider is what a pendant hangs from; a
    // bracelet and a ring have nothing to slide. The fieldset was gated only on
    // whether any add-on exists at all, so it showed on every piece (Henry,
    // 2026-08-29: "I still see slider add on ... only show if pendant is selected").
    //
    // The markup ships `hidden`, so with no JavaScript the question is never asked
    // rather than asked of everyone — the honest default for a control whose whole
    // correctness is conditional.
    const orientationWrap = $('[data-djh-hc-orientation-wrap]');
    // No hang question (Henry, 2026-10-05): a vertical and a horizontal pendant are separate products, so which one a piece is
    // arrives with the link and the control is never shown. Unseeded, a pendant starts vertical.
    if (orientationWrap) orientationWrap.hidden = true;

    const addonsWrap = $('[data-djh-hc-addons-wrap]');
    if (addonsWrap) {
      const piece = chosen ? chosen.value : '';
      const isPendant = piece === 'pendant';
      addonsWrap.hidden = !isPendant;

      // A HIDDEN CHECKBOX STILL SUBMITS. Tick Slider on a pendant, switch to a
      // bracelet, and the inquiry carries an add-on the form has stopped showing.
      // Clearing it is the difference between hiding a control and withdrawing it.
      if (!isPendant) {
        const stale = addonsWrap.querySelectorAll('input[name="addons"]:checked');
        if (stale.length) {
          for (let i = 0; i < stale.length; i += 1) stale[i].checked = false;
          // state.addons was read before this ran; re-read so the quote does not
          // keep pricing something the customer can no longer see.
          readForm();
        }
      }
    }
  }

  if (form) seedFromUrl();
  seedFromProduct();
  gatePieceType();
  readForm();
  renderWidths();
  renderGrades();
  renderSizeControl();
  renderConstruction();
  renderLettering();
  renderBorder();
  renderSizingLink();
  renderBar();
  renderEngravingCount();
  drawScale();
  refreshQuote();
})();
